/**
 * Pricing Debug PDF Document
 * 
 * Shows exactly what users see on the Public Proposal Page and PDF Download.
 * Admin-only, CRM-side visibility.
 */

import React from 'react';
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
} from '@react-pdf/renderer';
import { PricingDebugData, applyMargin } from '../../utils/pricingDebugUtils';
import { getEffectiveProjectOverheadPercent } from '../../utils/proposalCalculations';
import { estimatingRaw, listEstimatingAuditLines } from '../../utils/proposalDesignBreakdown';
import { listInstallEquipmentAuditLines, sumInstallEquipmentRaw } from '../../utils/proposalInstallEquipment';

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
    page: {
        padding: 30,
        fontSize: 9,
        fontFamily: 'Helvetica',
        backgroundColor: '#ffffff',
    },
    header: {
        marginBottom: 15,
        paddingBottom: 8,
        borderBottomWidth: 2,
        borderBottomColor: '#2c3e2f',
    },
    title: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#2c3e2f',
        marginBottom: 2,
    },
    subtitle: {
        fontSize: 9,
        color: '#666666',
    },
    section: {
        marginBottom: 12,
    },
    sectionTitle: {
        fontSize: 11,
        fontWeight: 'bold',
        color: '#2c3e2f',
        marginBottom: 6,
        paddingBottom: 3,
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    row: {
        flexDirection: 'row',
        paddingVertical: 3,
        borderBottomWidth: 1,
        borderBottomColor: '#f5f5f5',
    },
    rowHeader: {
        flexDirection: 'row',
        paddingVertical: 4,
        backgroundColor: '#f0f0f0',
        fontWeight: 'bold',
    },
    rowHighlight: {
        flexDirection: 'row',
        paddingVertical: 4,
        backgroundColor: '#e8f5e9',
        fontWeight: 'bold',
    },
    col50: { width: '50%' },
    colLabel: { width: '40%', paddingLeft: 4 },
    colRaw: { width: '30%', textAlign: 'right', paddingRight: 8 },
    colBillable: { width: '30%', textAlign: 'right', paddingRight: 4 },
    structureBox: {
        marginBottom: 10,
        padding: 8,
        borderWidth: 1,
        borderColor: '#e0e0e0',
        borderRadius: 3,
        backgroundColor: '#fafafa',
    },
    structureTitle: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#333333',
        marginBottom: 6,
    },
    lineItem: {
        flexDirection: 'row',
        paddingVertical: 2,
    },
    lineLabel: { width: '30%', paddingLeft: 8 },
    lineValue: { width: '23%', textAlign: 'right' },
    totalRow: {
        flexDirection: 'row',
        paddingTop: 4,
        marginTop: 4,
        borderTopWidth: 1,
        borderTopColor: '#ccc',
        fontWeight: 'bold',
    },
    subtotalRow: {
        flexDirection: 'row',
        paddingVertical: 3,
        backgroundColor: '#f5f5f5',
        marginTop: 2,
        marginBottom: 2,
    },
    summaryBox: {
        marginTop: 8,
        padding: 10,
        backgroundColor: '#2c3e2f',
        borderRadius: 3,
    },
    summaryRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    summaryLabel: {
        color: '#ffffff',
        fontSize: 10,
    },
    summaryValue: {
        color: '#ffffff',
        fontSize: 12,
        fontWeight: 'bold',
    },
    footer: {
        position: 'absolute',
        bottom: 15,
        left: 30,
        right: 30,
        fontSize: 7,
        color: '#999999',
        textAlign: 'center',
    },
    formula: {
        fontSize: 8,
        color: '#666666',
        fontStyle: 'italic',
        marginTop: 4,
        paddingLeft: 4,
    },
    infoBox: {
        marginTop: 8,
        padding: 8,
        backgroundColor: '#e3f2fd',
        borderRadius: 3,
        borderLeftWidth: 3,
        borderLeftColor: '#2196f3',
    },
    speciesBucketBox: {
        marginTop: 10,
        padding: 8,
        borderWidth: 1,
        borderColor: '#d0d0d0',
        borderRadius: 3,
        backgroundColor: '#fafafa',
    },
    // Node summary styles
    nodeRow: {
        flexDirection: 'row',
        paddingVertical: 3,
        borderBottomWidth: 1,
        borderBottomColor: '#f0f0f0',
    },
    nodeLabel: { width: '22%', paddingLeft: 4, fontSize: 8 },
    nodeValue: { width: '13%', textAlign: 'right', fontSize: 8 },
    nodeTotal: { width: '13%', textAlign: 'right', fontWeight: 'bold', fontSize: 8 },
});

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const fmt = (value: number | undefined): string => {
    if (value === undefined || isNaN(value)) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

// ============================================================================
// COMPONENT
// ============================================================================

interface Props {
    debugData: PricingDebugData;
    clientName?: string;
    proposalName?: string;
}

const PricingDebugPdfDocument: React.FC<Props> = ({
    debugData,
    clientName,
    proposalName,
}) => {
    // Get all unique species across all structures
    const allSpecies: string[] = [];
    debugData.structures.forEach(s => {
        s.speciesPricing.forEach(sp => {
            if (!allSpecies.includes(sp.speciesName)) {
                allSpecies.push(sp.speciesName);
            }
        });
    });

    // Compute per-species bucket totals
    const speciesBuckets = allSpecies.map(speciesName => {
        let designRaw = 0;
        let timberKitRaw = 0;
        let installRaw = 0;

        debugData.structures.forEach(structure => {
            const qty = structure.quantity || 1;

            // Design is always the same regardless of species
            designRaw += structure.designRaw * qty;

            if (structure.speciesPricing.find(s => s.speciesName === speciesName)) {
                const sp = structure.speciesPricing.find(s => s.speciesName === speciesName)!;
                // Timber Kit = Production + Materials + Freight + Overhead (varies by species via materials)
                timberKitRaw += sp.timberKitRaw * qty;
            } else {
                // Fallback to first species if this structure doesn't have this species
                const fallback = structure.speciesPricing[0];
                if (fallback) {
                    timberKitRaw += fallback.timberKitRaw * qty;
                }
            }

            // Install = Install + Travel (same regardless of species; install already includes overhead)
            installRaw += (structure.installRaw + structure.travelRaw) * qty;
        });

        // Global costs (same regardless of species)
        const globalCostsRaw = debugData.globalCosts?.totalGlobalCosts || 0;

        const baseBeforeProjectOverhead = designRaw + timberKitRaw + installRaw + globalCostsRaw;
        const poPct = getEffectiveProjectOverheadPercent(debugData.globalCosts);
        const projectOverheadRaw =
            poPct > 0 ? Math.round(baseBeforeProjectOverhead * (poPct / 100)) : 0;
        const grandTotalRaw = baseBeforeProjectOverhead + projectOverheadRaw;
        const margin = debugData.profitMarginPercent;

        return {
            speciesName,
            designRaw,
            timberKitRaw,
            installRaw,
            globalCostsRaw,
            projectOverheadRaw,
            grandTotalRaw,
            designBillable: Math.round(applyMargin(designRaw, margin)),
            timberKitBillable: Math.round(applyMargin(timberKitRaw, margin)),
            installBillable: Math.round(applyMargin(installRaw, margin)),
            globalCostsBillable: Math.round(applyMargin(globalCostsRaw, margin)),
            grandTotalBillable: Math.round(applyMargin(grandTotalRaw, margin)),
        };
    });

    return (
        <Document>
            {/* PAGE 1: Overview - Raw to Billable Calculation */}
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <Text style={styles.title}>Pricing Debug Report</Text>
                    <Text style={styles.subtitle}>
                        {proposalName || 'Proposal'} | {clientName || 'Client'} | Generated: {new Date(debugData.generatedAt).toLocaleString()}
                    </Text>
                </View>

                {/* Metadata Section */}
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Proposal Metadata</Text>
                    <View style={styles.row}>
                        <Text style={styles.col50}>Proposal ID</Text>
                        <Text style={styles.col50}>{debugData.proposalId}</Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.col50}>Type</Text>
                        <Text style={styles.col50}>{debugData.proposalType.toUpperCase()}</Text>
                    </View>
                    {debugData.proposalType === 'kit' ? (
                        <>
                            <View style={styles.row}>
                                <Text style={styles.col50}>Install Margin</Text>
                                <Text style={styles.col50}>{debugData.profitMarginPercent}% (×{debugData.marginMultiplier.toFixed(4)})</Text>
                            </View>
                            <View style={[styles.row, { backgroundColor: '#fff8e1' }]}>
                                <Text style={styles.col50}>Timber Kit Margin</Text>
                                <Text style={[styles.col50, { fontSize: 8 }]}>Baked into kitPricing (pre-set price, not raw × margin)</Text>
                            </View>
                        </>
                    ) : (
                        <>
                            <View style={styles.row}>
                                <Text style={styles.col50}>Profit Margin</Text>
                                <Text style={styles.col50}>{debugData.profitMarginPercent}%</Text>
                            </View>
                            <View style={styles.row}>
                                <Text style={styles.col50}>Margin Multiplier</Text>
                                <Text style={styles.col50}>×{debugData.marginMultiplier.toFixed(4)}</Text>
                            </View>
                        </>
                    )}
                    <View style={styles.row}>
                        <Text style={styles.col50}>Structures</Text>
                        <Text style={styles.col50}>{debugData.structureCount}</Text>
                    </View>
                    {debugData.activeSpecies && (
                        <View style={styles.row}>
                            <Text style={styles.col50}>Active Species</Text>
                            <Text style={styles.col50}>{debugData.activeSpecies}</Text>
                        </View>
                    )}
                    {debugData.proposalType === 'custom' && (
                        <Text style={styles.formula}>
                            Formula: Billable = Raw Cost ÷ (1 - {debugData.profitMarginPercent}/100) = Raw Cost × {debugData.marginMultiplier.toFixed(4)}
                        </Text>
                    )}
                </View>

                {debugData.proposalType === 'custom' ? (
                    /* CUSTOM: Show per-species bucket totals */
                    speciesBuckets.map((sb, idx) => (
                        <View key={sb.speciesName} style={[styles.speciesBucketBox, idx === 0 ? {} : { marginTop: 6 }]}>
                            <Text style={[styles.sectionTitle, { fontSize: 10 }]}>
                                Bucket Totals — {sb.speciesName}{idx === 0 ? ' ★ (default)' : ''}
                            </Text>
                            <View style={styles.rowHeader}>
                                <Text style={styles.colLabel}>Bucket</Text>
                                <Text style={styles.colRaw}>Raw Cost</Text>
                                <Text style={styles.colBillable}>Billable ({debugData.profitMarginPercent}%)</Text>
                            </View>
                            <View style={styles.row}>
                                <Text style={styles.colLabel}>Design</Text>
                                <Text style={styles.colRaw}>{fmt(sb.designRaw)}</Text>
                                <Text style={styles.colBillable}>{fmt(sb.designBillable)}</Text>
                            </View>
                            <View style={styles.row}>
                                <Text style={styles.colLabel}>Timber Kit (Prod+Mats+Freight+OH)</Text>
                                <Text style={styles.colRaw}>{fmt(sb.timberKitRaw)}</Text>
                                <Text style={styles.colBillable}>{fmt(sb.timberKitBillable)}</Text>
                            </View>
                            <View style={styles.row}>
                                <Text style={styles.colLabel}>Install (Install+Travel)</Text>
                                <Text style={styles.colRaw}>{fmt(sb.installRaw)}</Text>
                                <Text style={styles.colBillable}>{fmt(sb.installBillable)}</Text>
                            </View>
                            {sb.globalCostsRaw > 0 && (
                                <View style={[styles.row, { backgroundColor: '#fff8e1' }]}>
                                    <Text style={styles.colLabel}>Global Costs</Text>
                                    <Text style={styles.colRaw}>{fmt(sb.globalCostsRaw)}</Text>
                                    <Text style={styles.colBillable}>{fmt(sb.globalCostsBillable)}</Text>
                                </View>
                            )}
                            {sb.projectOverheadRaw > 0 && (
                                <View style={[styles.row, { backgroundColor: '#f0fdf4' }]}>
                                    <Text style={styles.colLabel}>Project overhead ({getEffectiveProjectOverheadPercent(debugData.globalCosts)}%)</Text>
                                    <Text style={styles.colRaw}>{fmt(sb.projectOverheadRaw)}</Text>
                                    <Text style={styles.colBillable}>{fmt(Math.round(applyMargin(sb.projectOverheadRaw, debugData.profitMarginPercent)))}</Text>
                                </View>
                            )}
                            <View style={styles.rowHighlight}>
                                <Text style={styles.colLabel}>GRAND TOTAL</Text>
                                <Text style={styles.colRaw}>{fmt(sb.grandTotalRaw)}</Text>
                                <Text style={styles.colBillable}>{fmt(sb.grandTotalBillable)}</Text>
                            </View>
                        </View>
                    ))
                ) : (
                    /* KIT: Show a note instead of bucket totals */
                    <View style={[styles.infoBox, { marginTop: 8 }]}>
                        <Text style={{ fontSize: 8 }}>
                            Kit proposals use pre-set prices from kitPricing (not raw cost × margin).
                            See "What The Page Shows" page for actual kit totals by structure.
                        </Text>
                    </View>
                )}

                <Text style={styles.footer}>INTERNAL DEBUG REPORT - NOT FOR DISTRIBUTION</Text>
            </Page>

            {/* PAGE 2: Structure Details with Species Columns */}
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <Text style={styles.title}>Structure Breakdowns by Species</Text>
                    <Text style={styles.subtitle}>{debugData.structureCount} structure(s) | {allSpecies.length} species</Text>
                </View>

                {debugData.structures.map((structure, index) => (
                    <View key={structure.id} style={styles.structureBox}>
                        <Text style={styles.structureTitle}>
                            {index + 1}. {structure.name} {structure.quantity > 1 ? `(Qty: ${structure.quantity})` : ''}
                        </Text>

                        {/* Header: Node + Species columns */}
                        <View style={[styles.lineItem, { backgroundColor: '#f0f0f0', marginBottom: 4 }]}>
                            <Text style={[styles.lineLabel, { fontWeight: 'bold' }]}>Node (Raw Cost)</Text>
                            {structure.speciesPricing.map((sp, i) => (
                                <Text key={i} style={[styles.lineValue, { fontWeight: 'bold', fontSize: 7 }]}>
                                    {sp.speciesName}{sp.isDefault ? ' ★' : ''}
                                </Text>
                            ))}
                        </View>

                        {/* ── DESIGN BUCKET ── */}
                        <View style={styles.lineItem}>
                            <Text style={styles.lineLabel}>Design</Text>
                            {structure.speciesPricing.map((_, i) => (
                                <Text key={i} style={styles.lineValue}>{fmt(structure.designRaw)}</Text>
                            ))}
                        </View>

                        {/* SUBTOTAL: Design */}
                        <View style={styles.subtotalRow}>
                            <Text style={[styles.lineLabel, { fontWeight: 'bold', fontSize: 8 }]}>= Design Subtotal</Text>
                            {structure.speciesPricing.map((_, i) => (
                                <Text key={i} style={[styles.lineValue, { fontWeight: 'bold', fontSize: 8 }]}>{fmt(structure.designRaw)}</Text>
                            ))}
                        </View>

                        {/* ── TIMBER KIT BUCKET: Prod + Mats + Freight + OH ── */}
                        <View style={styles.lineItem}>
                            <Text style={styles.lineLabel}>Production</Text>
                            {structure.speciesPricing.map((_, i) => (
                                <Text key={i} style={styles.lineValue}>{fmt(structure.productionRaw)}</Text>
                            ))}
                        </View>

                        <View style={styles.lineItem}>
                            <Text style={styles.lineLabel}>Materials</Text>
                            {structure.speciesPricing.map((sp, i) => (
                                <Text key={i} style={styles.lineValue}>{fmt(sp.materialsTotal)}</Text>
                            ))}
                        </View>

                        {/* Materials sub-breakdown */}
                        <View style={[styles.lineItem, { paddingLeft: 16 }]}>
                            <Text style={[styles.lineLabel, { fontSize: 7, color: '#666' }]}>└ Timber</Text>
                            {structure.speciesPricing.map((sp, i) => (
                                <Text key={i} style={[styles.lineValue, { fontSize: 7, color: '#666' }]}>{fmt(sp.timberTotal)}</Text>
                            ))}
                        </View>
                        <View style={[styles.lineItem, { paddingLeft: 16 }]}>
                            <Text style={[styles.lineLabel, { fontSize: 7, color: '#666' }]}>└ Other (pegs, etc)</Text>
                            {structure.speciesPricing.map((_, i) => (
                                <Text key={i} style={[styles.lineValue, { fontSize: 7, color: '#666' }]}>{fmt(structure.materialsRaw - structure.timberTotal)}</Text>
                            ))}
                        </View>

                        <View style={styles.lineItem}>
                            <Text style={styles.lineLabel}>Freight</Text>
                            {structure.speciesPricing.map((_, i) => (
                                <Text key={i} style={styles.lineValue}>{fmt(structure.freightRaw)}</Text>
                            ))}
                        </View>

                        <View style={styles.lineItem}>
                            <Text style={styles.lineLabel}>Overhead</Text>
                            {structure.speciesPricing.map((_, i) => (
                                <Text key={i} style={styles.lineValue}>{fmt(structure.overheadRaw)}</Text>
                            ))}
                        </View>

                        {/* SUBTOTAL: Timber Kit */}
                        <View style={styles.subtotalRow}>
                            <Text style={[styles.lineLabel, { fontWeight: 'bold', fontSize: 8 }]}>= Timber Kit Subtotal</Text>
                            {structure.speciesPricing.map((sp, i) => (
                                <Text key={i} style={[styles.lineValue, { fontWeight: 'bold', fontSize: 8 }]}>{fmt(sp.timberKitRaw)}</Text>
                            ))}
                        </View>

                        {/* ── INSTALL BUCKET: Install + Travel ── */}
                        <View style={styles.lineItem}>
                            <Text style={styles.lineLabel}>Install</Text>
                            {structure.speciesPricing.map((_, i) => (
                                <Text key={i} style={styles.lineValue}>{fmt(structure.installRaw)}</Text>
                            ))}
                        </View>

                        <View style={styles.lineItem}>
                            <Text style={styles.lineLabel}>Travel</Text>
                            {structure.speciesPricing.map((_, i) => (
                                <Text key={i} style={styles.lineValue}>{fmt(structure.travelRaw)}</Text>
                            ))}
                        </View>

                        {/* SUBTOTAL: Install */}
                        <View style={styles.subtotalRow}>
                            <Text style={[styles.lineLabel, { fontWeight: 'bold', fontSize: 8 }]}>= Install Subtotal</Text>
                            {structure.speciesPricing.map((_, i) => (
                                <Text key={i} style={[styles.lineValue, { fontWeight: 'bold', fontSize: 8 }]}>
                                    {fmt(structure.installRaw + structure.travelRaw)}
                                </Text>
                            ))}
                        </View>

                        {/* STRUCTURE TOTAL */}
                        <View style={styles.totalRow}>
                            <Text style={[styles.lineLabel, { fontWeight: 'bold' }]}>STRUCTURE RAW TOTAL</Text>
                            {structure.speciesPricing.map((sp, i) => (
                                <Text key={i} style={[styles.lineValue, { fontWeight: 'bold' }]}>{fmt(sp.structureRawTotal)}</Text>
                            ))}
                        </View>

                        {structure.quantity > 1 && (
                            <View style={[styles.lineItem, { marginTop: 4 }]}>
                                <Text style={[styles.lineLabel, { fontSize: 8, color: '#666' }]}>× {structure.quantity} qty =</Text>
                                {structure.speciesPricing.map((sp, i) => (
                                    <Text key={i} style={[styles.lineValue, { fontSize: 8, color: '#666' }]}>{fmt(sp.structureRawTotalWithQty)}</Text>
                                ))}
                            </View>
                        )}
                    </View>
                ))}

                <Text style={styles.footer}>INTERNAL DEBUG REPORT - NOT FOR DISTRIBUTION</Text>
            </Page>

            {/* PAGE 3: ALL COSTS BY NODE — detailed field breakdown with rates */}
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <Text style={styles.title}>All Costs by Node</Text>
                    <Text style={styles.subtitle}>Every field value with rate calculations, per structure</Text>
                </View>

                {/* DESIGN NODE */}
                <View style={[styles.structureBox, { marginBottom: 8 }]}>
                    <Text style={[styles.structureTitle, { marginBottom: 4 }]}>Design</Text>
                    {debugData.structures.map((s, i) => {
                        const d = s.designNode;
                        const r = debugData.rates;
                        const apmRate = r.apmRate ?? 40;
                        const pmSub = d.pmHours * r.pmRate;
                        const apmSub = (d.apmHours ?? 0) * apmRate;
                        const leadSub = d.leadDesignerHours * r.leadDesignerRate;
                        const supportSub = d.supportDesignerHours * r.supportDesignerRate;
                        const engSub = d.engineeringMode === 'lumpSum'
                            ? (d.engineeringLumpSum || 0)
                            : d.engineeringHours * r.engineeringRate;
                        return (
                            <View key={s.id} style={{ marginBottom: i < debugData.structures.length - 1 ? 8 : 0 }}>
                                <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#333', marginBottom: 2 }}>{s.name}</Text>
                                {d.pmHours > 0 && (
                                    <View style={styles.nodeRow}>
                                        <Text style={[styles.nodeLabel, { width: '60%' }]}>PM: {d.pmHours}h × {fmt(r.pmRate)}/hr</Text>
                                        <Text style={[styles.nodeValue, { width: '40%' }]}>{fmt(pmSub)}</Text>
                                    </View>
                                )}
                                {(d.apmHours ?? 0) > 0 && (
                                    <View style={styles.nodeRow}>
                                        <Text style={[styles.nodeLabel, { width: '60%' }]}>APM: {d.apmHours}h × {fmt(apmRate)}/hr</Text>
                                        <Text style={[styles.nodeValue, { width: '40%' }]}>{fmt(apmSub)}</Text>
                                    </View>
                                )}
                                {d.leadDesignerHours > 0 && (
                                    <View style={styles.nodeRow}>
                                        <Text style={[styles.nodeLabel, { width: '60%' }]}>Lead Designer: {d.leadDesignerHours}h × {fmt(r.leadDesignerRate)}/hr</Text>
                                        <Text style={[styles.nodeValue, { width: '40%' }]}>{fmt(leadSub)}</Text>
                                    </View>
                                )}
                                {d.supportDesignerHours > 0 && (
                                    <View style={styles.nodeRow}>
                                        <Text style={[styles.nodeLabel, { width: '60%' }]}>Support Designer: {d.supportDesignerHours}h × {fmt(r.supportDesignerRate)}/hr</Text>
                                        <Text style={[styles.nodeValue, { width: '40%' }]}>{fmt(supportSub)}</Text>
                                    </View>
                                )}
                                {d.engineeringMode === 'lumpSum' && (d.engineeringLumpSum || 0) > 0 && (
                                    <View style={styles.nodeRow}>
                                        <Text style={[styles.nodeLabel, { width: '60%' }]}>Engineering lump sum</Text>
                                        <Text style={[styles.nodeValue, { width: '40%' }]}>{fmt(engSub)}</Text>
                                    </View>
                                )}
                                {d.engineeringMode !== 'lumpSum' && d.engineeringHours > 0 && (
                                    <View style={styles.nodeRow}>
                                        <Text style={[styles.nodeLabel, { width: '60%' }]}>Engineering: {d.engineeringHours}h × {fmt(r.engineeringRate)}/hr</Text>
                                        <Text style={[styles.nodeValue, { width: '40%' }]}>{fmt(engSub)}</Text>
                                    </View>
                                )}
                                <View style={[styles.nodeRow, { backgroundColor: '#f5f5f5' }]}>
                                    <Text style={[styles.nodeLabel, { width: '60%', fontWeight: 'bold' }]}>Node Total</Text>
                                    <Text style={[styles.nodeTotal, { width: '40%' }]}>{fmt(d.total)}</Text>
                                </View>
                            </View>
                        );
                    })}
                </View>

                {/* PRODUCTION NODE */}
                <View style={[styles.structureBox, { marginBottom: 8 }]}>
                    <Text style={[styles.structureTitle, { marginBottom: 4 }]}>Production</Text>
                    {debugData.structures.map((s, i) => {
                        const p = s.productionNode;
                        const ratePerBf = p.boardFeet > 0 ? p.total / p.boardFeet : 0;
                        return (
                            <View key={s.id} style={{ marginBottom: i < debugData.structures.length - 1 ? 8 : 0 }}>
                                <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#333', marginBottom: 2 }}>{s.name}</Text>
                                <View style={[styles.nodeRow, { backgroundColor: '#f5f5f5' }]}>
                                    <Text style={[styles.nodeLabel, { width: '60%', fontWeight: 'bold' }]}>{p.boardFeet.toLocaleString()} bf × {fmt(ratePerBf)}/bf</Text>
                                    <Text style={[styles.nodeTotal, { width: '40%' }]}>{fmt(p.total)}</Text>
                                </View>
                            </View>
                        );
                    })}
                </View>

                {/* TIMBER PACKAGE — by species */}
                <View style={[styles.structureBox, { marginBottom: 8 }]}>
                    <Text style={[styles.structureTitle, { marginBottom: 4 }]}>Timber Package (by species)</Text>
                    {debugData.structures.map((s, i) => {
                        const m = s.materialsNode;
                        return (
                            <View key={s.id} style={{ marginBottom: i < debugData.structures.length - 1 ? 10 : 0 }}>
                                <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#333', marginBottom: 3 }}>{s.name}</Text>

                                {/* Default species timber list */}
                                <Text style={{ fontSize: 7, fontWeight: 'bold', color: '#555', marginBottom: 2, paddingLeft: 4 }}>
                                    {s.defaultSpeciesName} (default) — {m.totalBoardFeet.toLocaleString()} bf
                                </Text>
                                {m.timberList.map((item, ti) => (
                                    <View key={item.id || ti} style={styles.nodeRow}>
                                        <Text style={[styles.nodeLabel, { width: '40%', fontSize: 7, paddingLeft: 8 }]}>
                                            {item.name} ({item.dimensions})
                                        </Text>
                                        <Text style={[styles.nodeValue, { width: '20%', fontSize: 7, color: '#666' }]}>
                                            {item.species} {item.grade}
                                            {item.moistureContent === 'green' ? ' · Green' : item.moistureContent === 'kiln_dried' ? ' · Kiln dried' : ''}
                                        </Text>
                                        <Text style={[styles.nodeValue, { width: '20%', fontSize: 7, color: '#666' }]}>
                                            {item.quantity} × {fmt(item.unitCost)}
                                        </Text>
                                        <Text style={[styles.nodeValue, { width: '20%', fontSize: 7 }]}>
                                            {fmt(item.total)}
                                        </Text>
                                    </View>
                                ))}
                                <View style={[styles.nodeRow, { backgroundColor: '#f0f4f8' }]}>
                                    <Text style={[styles.nodeLabel, { width: '60%', fontWeight: 'bold', fontSize: 7, paddingLeft: 8 }]}>
                                        {s.defaultSpeciesName} Timber Total
                                    </Text>
                                    <Text style={[styles.nodeTotal, { width: '40%', fontSize: 7 }]}>{fmt(m.timberTotal)}</Text>
                                </View>

                                {/* Alternate species */}
                                {(m.alternateSpecies || []).map(alt => (
                                    <View key={alt.speciesName} style={{ marginTop: 4 }}>
                                        <Text style={{ fontSize: 7, fontWeight: 'bold', color: '#555', marginBottom: 2, paddingLeft: 4 }}>
                                            {alt.speciesName} (alternate) — {alt.totalBoardFeet.toLocaleString()} bf
                                        </Text>
                                        {alt.timberList.map((item, ti) => (
                                            <View key={item.id || ti} style={styles.nodeRow}>
                                                <Text style={[styles.nodeLabel, { width: '40%', fontSize: 7, paddingLeft: 8 }]}>
                                                    {item.name} ({item.dimensions})
                                                </Text>
                                                <Text style={[styles.nodeValue, { width: '20%', fontSize: 7, color: '#666' }]}>
                                                    {item.species} {item.grade}
                                                    {item.moistureContent === 'green' ? ' · Green' : item.moistureContent === 'kiln_dried' ? ' · Kiln dried' : ''}
                                                </Text>
                                                <Text style={[styles.nodeValue, { width: '20%', fontSize: 7, color: '#666' }]}>
                                                    {item.quantity} × {fmt(item.unitCost)}
                                                </Text>
                                                <Text style={[styles.nodeValue, { width: '20%', fontSize: 7 }]}>
                                                    {fmt(item.total)}
                                                </Text>
                                            </View>
                                        ))}
                                        <View style={[styles.nodeRow, { backgroundColor: '#f0f4f8' }]}>
                                            <Text style={[styles.nodeLabel, { width: '60%', fontWeight: 'bold', fontSize: 7, paddingLeft: 8 }]}>
                                                {alt.speciesName} Timber Total
                                            </Text>
                                            <Text style={[styles.nodeTotal, { width: '40%', fontSize: 7 }]}>{fmt(alt.timberTotal)}</Text>
                                        </View>
                                    </View>
                                ))}
                            </View>
                        );
                    })}
                </View>

                {/* NON-TIMBER MATERIALS (Pegs, Post Bases, Fasteners, Decking) */}
                <View style={[styles.structureBox, { marginBottom: 8 }]}>
                    <Text style={[styles.structureTitle, { marginBottom: 4 }]}>Non-Timber Materials</Text>
                    {debugData.structures.map((s, i) => {
                        const m = s.materialsNode;
                        const nonTimber = m.pegsCost + m.postBasesTotal + m.fastenerPackageCost + (m.deckingTotal || 0);
                        return (
                            <View key={s.id} style={{ marginBottom: i < debugData.structures.length - 1 ? 8 : 0 }}>
                                <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#333', marginBottom: 2 }}>{s.name}</Text>
                                <View style={styles.nodeRow}>
                                    <Text style={[styles.nodeLabel, { width: '60%' }]}>Pegs</Text>
                                    <Text style={[styles.nodeValue, { width: '40%' }]}>{fmt(m.pegsCost)}</Text>
                                </View>
                                <View style={styles.nodeRow}>
                                    <Text style={[styles.nodeLabel, { width: '60%' }]}>Post Bases: {m.postBasesQty} × {fmt(m.postBasesUnitCost)}</Text>
                                    <Text style={[styles.nodeValue, { width: '40%' }]}>{fmt(m.postBasesTotal)}</Text>
                                </View>
                                <View style={styles.nodeRow}>
                                    <Text style={[styles.nodeLabel, { width: '60%' }]}>Fastener Package</Text>
                                    <Text style={[styles.nodeValue, { width: '40%' }]}>{fmt(m.fastenerPackageCost)}</Text>
                                </View>
                                {(m.deckingTotal || 0) > 0 && (
                                    <View style={styles.nodeRow}>
                                        <Text style={[styles.nodeLabel, { width: '60%' }]}>
                                            Decking: {m.deckingSqFt || 0} sq ft @ {fmt(m.deckingPricePerLinFt || 0)}/lf ({m.deckingWidthInches || 0}&quot; boards)
                                        </Text>
                                        <Text style={[styles.nodeValue, { width: '40%' }]}>{fmt(m.deckingTotal || 0)}</Text>
                                    </View>
                                )}
                                <View style={[styles.nodeRow, { backgroundColor: '#f5f5f5' }]}>
                                    <Text style={[styles.nodeLabel, { width: '60%', fontWeight: 'bold' }]}>Non-Timber Total</Text>
                                    <Text style={[styles.nodeTotal, { width: '40%' }]}>{fmt(nonTimber)}</Text>
                                </View>
                                <View style={[styles.nodeRow, { backgroundColor: '#e8f5e9' }]}>
                                    <Text style={[styles.nodeLabel, { width: '60%', fontWeight: 'bold' }]}>Materials Node Total (Timber + Non-Timber)</Text>
                                    <Text style={[styles.nodeTotal, { width: '40%' }]}>{fmt(m.total)}</Text>
                                </View>
                            </View>
                        );
                    })}
                </View>

                {/* FREIGHT NODE */}
                <View style={[styles.structureBox, { marginBottom: 8 }]}>
                    <Text style={[styles.structureTitle, { marginBottom: 4 }]}>Freight</Text>
                    {debugData.structures.map((s, i) => {
                        const f = s.freightNode;
                        const legA = f.milesToAspers * f.costPerMileToAspers + f.flatFeeToAspers;
                        const legB = f.milesToJobSite * f.costPerMileToJobSite + f.flatFeeToJobSite;
                        return (
                            <View key={s.id} style={{ marginBottom: i < debugData.structures.length - 1 ? 8 : 0 }}>
                                <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#333', marginBottom: 2 }}>{s.name}</Text>
                                <View style={styles.nodeRow}>
                                    <Text style={[styles.nodeLabel, { width: '60%' }]}>Leg 1 → Aspers: {f.milesToAspers} mi × {fmt(f.costPerMileToAspers)}/mi + {fmt(f.flatFeeToAspers)} flat</Text>
                                    <Text style={[styles.nodeValue, { width: '40%' }]}>{fmt(legA)}</Text>
                                </View>
                                <View style={styles.nodeRow}>
                                    <Text style={[styles.nodeLabel, { width: '60%' }]}>Leg 2 → Job Site: {f.milesToJobSite} mi × {fmt(f.costPerMileToJobSite)}/mi + {fmt(f.flatFeeToJobSite)} flat</Text>
                                    <Text style={[styles.nodeValue, { width: '40%' }]}>{fmt(legB)}</Text>
                                </View>
                                <View style={[styles.nodeRow, { backgroundColor: '#f5f5f5' }]}>
                                    <Text style={[styles.nodeLabel, { width: '60%', fontWeight: 'bold' }]}>Node Total</Text>
                                    <Text style={[styles.nodeTotal, { width: '40%' }]}>{fmt(f.total)}</Text>
                                </View>
                            </View>
                        );
                    })}
                </View>

                {/* OVERHEAD NODE */}
                <View style={[styles.structureBox, { marginBottom: 8 }]}>
                    <Text style={[styles.structureTitle, { marginBottom: 4 }]}>Overhead</Text>
                    {debugData.structures.map((s, i) => {
                        const o = s.overheadNode;
                        const dailyRate = o.days > 0 ? o.total / o.days : 0;
                        return (
                            <View key={s.id} style={{ marginBottom: i < debugData.structures.length - 1 ? 8 : 0 }}>
                                <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#333', marginBottom: 2 }}>{s.name}</Text>
                                <View style={[styles.nodeRow, { backgroundColor: '#f5f5f5' }]}>
                                    <Text style={[styles.nodeLabel, { width: '60%', fontWeight: 'bold' }]}>{o.days} days × {fmt(dailyRate)}/day</Text>
                                    <Text style={[styles.nodeTotal, { width: '40%' }]}>{fmt(o.total)}</Text>
                                </View>
                            </View>
                        );
                    })}
                </View>

                {/* INSTALL NODE */}
                <View style={[styles.structureBox, { marginBottom: 8 }]}>
                    <Text style={[styles.structureTitle, { marginBottom: 4 }]}>Install</Text>
                    {debugData.structures.map((s, i) => {
                        const inst = s.installNode;
                        const laborSubtotal = inst.days * inst.teamLaborPerDay;
                        const equipmentTotal = sumInstallEquipmentRaw(inst);
                        const overheadAmount = inst.total - laborSubtotal - equipmentTotal;
                        const ohRate = inst.days > 0 ? overheadAmount / inst.days : 0;
                        return (
                            <View key={s.id} style={{ marginBottom: i < debugData.structures.length - 1 ? 8 : 0 }}>
                                <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#333', marginBottom: 2 }}>{s.name}</Text>
                                <View style={styles.nodeRow}>
                                    <Text style={[styles.nodeLabel, { width: '60%' }]}>Team Labor: {inst.days}d × {fmt(inst.teamLaborPerDay)}/day</Text>
                                    <Text style={[styles.nodeValue, { width: '40%' }]}>{fmt(laborSubtotal)}</Text>
                                </View>
                                <View style={styles.nodeRow}>
                                    <Text style={[styles.nodeLabel, { width: '60%' }]}>Overhead: {inst.days}d × {fmt(ohRate)}/day</Text>
                                    <Text style={[styles.nodeValue, { width: '40%' }]}>{fmt(overheadAmount)}</Text>
                                </View>
                                {listInstallEquipmentAuditLines(inst).map(row => (
                                    <View key={row.label} style={styles.nodeRow}>
                                        <Text style={[styles.nodeLabel, { width: '60%' }]}>{row.label}</Text>
                                        <Text style={[styles.nodeValue, { width: '40%' }]}>{fmt(row.amount)}</Text>
                                    </View>
                                ))}
                                <View style={[styles.nodeRow, { backgroundColor: '#f5f5f5' }]}>
                                    <Text style={[styles.nodeLabel, { width: '60%', fontWeight: 'bold' }]}>Node Total</Text>
                                    <Text style={[styles.nodeTotal, { width: '40%' }]}>{fmt(inst.total)}</Text>
                                </View>
                            </View>
                        );
                    })}
                </View>

                {/* TRAVEL NODE */}
                <View style={[styles.structureBox, { marginBottom: 8 }]}>
                    <Text style={[styles.structureTitle, { marginBottom: 4 }]}>Travel</Text>
                    {debugData.structures.map((s, i) => {
                        const t = s.travelNode;
                        const hotelTotal = t.hotelNights * t.hotelRatePerNight;
                        // Actual formula: hotel + crewSize × perDiem × (hotelNights + 1) + fuelBudget
                        const perDiemDays = t.hotelNights + 1; // always +1 for travel day
                        const perDiemTotal = t.crewSize * t.perDiemPerPersonPerDay * perDiemDays;
                        return (
                            <View key={s.id} style={{ marginBottom: i < debugData.structures.length - 1 ? 8 : 0 }}>
                                <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#333', marginBottom: 2 }}>{s.name}</Text>
                                <View style={styles.nodeRow}>
                                    <Text style={[styles.nodeLabel, { width: '60%' }]}>Hotel: {t.hotelNights} nights × {fmt(t.hotelRatePerNight)}/night</Text>
                                    <Text style={[styles.nodeValue, { width: '40%' }]}>{fmt(hotelTotal)}</Text>
                                </View>
                                <View style={styles.nodeRow}>
                                    <Text style={[styles.nodeLabel, { width: '60%' }]}>Per Diem: {t.crewSize} crew × {fmt(t.perDiemPerPersonPerDay)}/day × {perDiemDays}d (nights+1)</Text>
                                    <Text style={[styles.nodeValue, { width: '40%' }]}>{fmt(perDiemTotal)}</Text>
                                </View>
                                <View style={styles.nodeRow}>
                                    <Text style={[styles.nodeLabel, { width: '60%' }]}>Fuel Budget</Text>
                                    <Text style={[styles.nodeValue, { width: '40%' }]}>{fmt(t.fuelBudget)}</Text>
                                </View>
                                <View style={styles.nodeRow}>
                                    <Text style={[styles.nodeLabel, { width: '60%' }]}>Miles to Job Site (round trip)</Text>
                                    <Text style={[styles.nodeValue, { width: '40%' }]}>{t.milesToJobSite} mi</Text>
                                </View>
                                <View style={[styles.nodeRow, { backgroundColor: '#f5f5f5' }]}>
                                    <Text style={[styles.nodeLabel, { width: '60%', fontWeight: 'bold' }]}>Node Total</Text>
                                    <Text style={[styles.nodeTotal, { width: '40%' }]}>{fmt(t.total)}</Text>
                                </View>
                            </View>
                        );
                    })}
                </View>

                <Text style={styles.footer}>INTERNAL DEBUG REPORT - NOT FOR DISTRIBUTION</Text>
            </Page>

            {/* PAGE 4: WHAT USERS SEE - Public Page & PDF Values */}
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <Text style={styles.title}>What Users See</Text>
                    <Text style={styles.subtitle}>Exact values displayed on Public Proposal Page and PDF Download</Text>
                </View>

                <View style={styles.infoBox}>
                    <Text style={{ fontSize: 8 }}>
                        {debugData.proposalType === 'kit'
                            ? 'Kit proposal: TimberKit from kitPricing, Install recalculated from raw nodes. billablePrices are NOT used for kit display.'
                            : 'The Public Proposal Page reads from stored billablePrices (below). The customer-facing PDF Download recalculates from raw node totals — see "Calculation Paths" page for exact formulas.'
                        }
                    </Text>
                </View>

                {debugData.proposalType === 'kit' ? (
                    /* KIT: Show actual page values, not billablePrices */
                    (() => {
                        const activeKitPricing = debugData.kitPricing
                            ? debugData.kitPricing.find(kp => kp.speciesName === debugData.activeSpecies) || debugData.kitPricing[0]
                            : undefined;
                        const isMultiStructure = debugData.structures.length > 1;
                        let grandTK = 0;
                        let grandInstall = 0;

                        return (
                            <>
                                {debugData.structures.map((s, i) => {
                                    const qty = s.quantity;
                                    const margin = debugData.profitMarginPercent;
                                    let tk = 0;
                                    if (activeKitPricing) {
                                        if (isMultiStructure && activeKitPricing.structurePricing?.[i]) {
                                            tk = Math.round(activeKitPricing.structurePricing[i].cost || 0);
                                        } else if (isMultiStructure) {
                                            tk = Math.round((activeKitPricing.totalCost || 0) / debugData.structures.length);
                                        } else {
                                            tk = Math.round(activeKitPricing.totalCost || 0);
                                        }
                                    }
                                    const install = Math.round(applyMargin(
                                        (s.installRaw + s.travelRaw) * qty, margin
                                    ));
                                    grandTK += tk;
                                    grandInstall += install;
                                    const total = tk + install;

                                    return (
                                        <View key={s.id} style={[styles.structureBox, { marginTop: 8 }]}>
                                            <Text style={styles.structureTitle}>{i + 1}. {s.name}{qty > 1 ? ` (×${qty})` : ''}</Text>
                                            <View style={styles.rowHeader}>
                                                <Text style={{ width: '50%', paddingLeft: 4, fontSize: 9 }}>Category</Text>
                                                <Text style={{ width: '25%', textAlign: 'right', paddingRight: 4, fontSize: 9 }}>Source</Text>
                                                <Text style={{ width: '25%', textAlign: 'right', paddingRight: 4, fontSize: 9 }}>Displayed</Text>
                                            </View>
                                            <View style={styles.row}>
                                                <Text style={{ width: '50%', paddingLeft: 4 }}>Design</Text>
                                                <Text style={{ width: '25%', textAlign: 'right', paddingRight: 4, fontSize: 8, color: '#999' }}>in kit price</Text>
                                                <Text style={{ width: '25%', textAlign: 'right', paddingRight: 4 }}>Included</Text>
                                            </View>
                                            <View style={styles.row}>
                                                <Text style={{ width: '50%', paddingLeft: 4 }}>Timber Kit</Text>
                                                <Text style={{ width: '25%', textAlign: 'right', paddingRight: 4, fontSize: 8, color: '#999' }}>kitPricing</Text>
                                                <Text style={{ width: '25%', textAlign: 'right', paddingRight: 4 }}>{fmt(tk)}</Text>
                                            </View>
                                            <View style={styles.row}>
                                                <Text style={{ width: '50%', paddingLeft: 4 }}>Install</Text>
                                                <Text style={{ width: '25%', textAlign: 'right', paddingRight: 4, fontSize: 8, color: '#999' }}>raw nodes</Text>
                                                <Text style={{ width: '25%', textAlign: 'right', paddingRight: 4 }}>{fmt(install)}</Text>
                                            </View>
                                            <View style={[styles.row, { fontWeight: 'bold', backgroundColor: '#f5f5f5' }]}>
                                                <Text style={{ width: '50%', paddingLeft: 4, fontWeight: 'bold' }}>Structure Total</Text>
                                                <Text style={{ width: '25%', textAlign: 'right', paddingRight: 4 }}></Text>
                                                <Text style={{ width: '25%', textAlign: 'right', paddingRight: 4, fontWeight: 'bold' }}>{fmt(total)}</Text>
                                            </View>
                                        </View>
                                    );
                                })}
                                <View style={[styles.summaryBox, { marginTop: 12 }]}>
                                    <View style={styles.summaryRow}>
                                        <Text style={styles.summaryLabel}>Total Displayed to Customer</Text>
                                        <Text style={styles.summaryValue}>{fmt(grandTK + grandInstall)}</Text>
                                    </View>
                                    <View style={{ flexDirection: 'row', justifyContent: 'flex-end', paddingRight: 8 }}>
                                        <Text style={{ fontSize: 7, color: '#999' }}>
                                            TK: {fmt(grandTK)} + Install: {fmt(grandInstall)}
                                        </Text>
                                    </View>
                                </View>
                            </>
                        );
                    })()
                ) : (
                    /* CUSTOM: Show billablePrices as before */
                    <>
                        {debugData.storedBillablePrices.length === 0 ? (
                            <View style={[styles.section, { marginTop: 12 }]}>
                                <Text style={{ color: '#999', fontStyle: 'italic' }}>No stored billable prices found. This is a legacy proposal - values are calculated on-the-fly.</Text>
                            </View>
                        ) : (
                            debugData.storedBillablePrices.map((stored, index) => {
                                const displayedInstall = stored.install + stored.travel;
                                return (
                                    <View key={stored.structureId} style={[styles.structureBox, { marginTop: 8 }]}>
                                        <Text style={styles.structureTitle}>{index + 1}. {stored.structureName}</Text>
                                        <View style={styles.rowHeader}>
                                            <Text style={{ width: '50%', paddingLeft: 4, fontSize: 9 }}>Category</Text>
                                            <Text style={{ width: '50%', textAlign: 'right', paddingRight: 4, fontSize: 9 }}>Displayed Value</Text>
                                        </View>
                                        <View style={styles.row}>
                                            <Text style={{ width: '50%', paddingLeft: 4 }}>Design</Text>
                                            <Text style={{ width: '50%', textAlign: 'right', paddingRight: 4 }}>{fmt(stored.design)}</Text>
                                        </View>
                                        <View style={styles.row}>
                                            <Text style={{ width: '50%', paddingLeft: 4 }}>Timber Kit</Text>
                                            <Text style={{ width: '50%', textAlign: 'right', paddingRight: 4 }}>{fmt(stored.timberKit)}</Text>
                                        </View>
                                        <View style={styles.row}>
                                            <Text style={{ width: '50%', paddingLeft: 4 }}>Install</Text>
                                            <Text style={{ width: '50%', textAlign: 'right', paddingRight: 4 }}>{fmt(displayedInstall)}</Text>
                                        </View>
                                        <View style={[styles.row, { fontWeight: 'bold', backgroundColor: '#f5f5f5' }]}>
                                            <Text style={{ width: '50%', paddingLeft: 4, fontWeight: 'bold' }}>Structure Total</Text>
                                            <Text style={{ width: '50%', textAlign: 'right', paddingRight: 4, fontWeight: 'bold' }}>{fmt(stored.subtotal)}</Text>
                                        </View>
                                        {stored.speciesOptions && Object.keys(stored.speciesOptions).length > 0 && (
                                            <View style={{ marginTop: 8 }}>
                                                <Text style={{ fontSize: 8, fontWeight: 'bold', marginBottom: 4, backgroundColor: '#e8f5e9', padding: 4 }}>
                                                    Timber Kit by Species (when customer selects a species):
                                                </Text>
                                                {Object.entries(stored.speciesOptions).map(([species, price]) => (
                                                    <View key={species} style={styles.row}>
                                                        <Text style={{ width: '50%', paddingLeft: 8, fontSize: 8 }}>{species}</Text>
                                                        <Text style={{ width: '50%', textAlign: 'right', paddingRight: 4, fontSize: 8 }}>{fmt(price as number)}</Text>
                                                    </View>
                                                ))}
                                            </View>
                                        )}
                                    </View>
                                );
                            })
                        )}
                        {debugData.storedBillablePrices.length > 0 && (
                            <View style={[styles.summaryBox, { marginTop: 12 }]}>
                                <View style={styles.summaryRow}>
                                    <Text style={styles.summaryLabel}>Total Displayed to Customer (all structures)</Text>
                                    <Text style={styles.summaryValue}>
                                        {fmt(debugData.storedBillablePrices.reduce((sum, s) => sum + s.subtotal, 0))}
                                    </Text>
                                </View>
                            </View>
                        )}
                    </>
                )}

                <Text style={styles.footer}>INTERNAL DEBUG REPORT - NOT FOR DISTRIBUTION</Text>
            </Page>

            {/* PAGE 5: CALCULATION PATHS REFERENCE */}
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <Text style={styles.title}>Calculation Paths — {debugData.proposalType === 'kit' ? 'KIT' : 'CUSTOM'} Proposal</Text>
                    <Text style={styles.subtitle}>Exact functions, formulas, and code paths for every total — 3 consumers, 3 different paths</Text>
                </View>

                {/* Overview */}
                <View style={[styles.infoBox, { marginBottom: 10 }]}>
                    <Text style={{ fontSize: 7.5, lineHeight: 1.4 }}>
                        This proposal is type: {debugData.proposalType.toUpperCase()}. Paths shown below are for this type.{'\n'}
                        ① Save-time (ProposalBuilderPage.tsx handleSaveProposal) → writes billablePrices to Firestore{'\n'}
                        ② Public Page (PublicProposalPage.tsx) → grandTotals useMemo{'\n'}
                        ③ PDF Download (ProposalPdfDocument.tsx) → grandTotals IIFE
                    </Text>
                </View>

                {debugData.proposalType === 'custom' ? (
                    <>
                        {/* ============ CUSTOM PROPOSAL PATHS ============ */}

                        {/* PATH 1: SAVE-TIME (Custom) */}
                        <View style={[styles.structureBox, { marginBottom: 8 }]}>
                            <Text style={[styles.structureTitle, { marginBottom: 2 }]}>① Save-Time — handleSaveProposal() → billablePrices</Text>
                            <Text style={{ fontSize: 7, color: '#666', marginBottom: 6 }}>
                                Per-nodeSet billablePrices saved to Firestore. Public Page reads these directly.
                            </Text>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontWeight: 'bold', fontSize: 7 }]}>Bucket</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontWeight: 'bold', fontSize: 7 }]}>Formula</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>design</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>round( design.total / (1 - margin/100) × qty )</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>timberKit</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>round( (materials + production + freight + overhead) / (1 - margin/100) × qty )</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>install</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>round( (install.total + install.overheadTotal) / (1 - margin/100) × qty )  ⚠ travel NOT included</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>travel</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>round( travel.total / (1 - margin/100) × qty )  ← separate field</Text>
                            </View>
                            <View style={[styles.nodeRow, { backgroundColor: '#f5f5f5' }]}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontWeight: 'bold', fontSize: 7 }]}>subtotal</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontWeight: 'bold', fontSize: 6.5 }]}>design + timberKit + install + travel</Text>
                            </View>
                        </View>

                        {/* PATH 2: PUBLIC PAGE (Custom) */}
                        <View style={[styles.structureBox, { marginBottom: 8 }]}>
                            <Text style={[styles.structureTitle, { marginBottom: 2 }]}>② Public Page — calculateBuckets() → grandTotals</Text>
                            <Text style={{ fontSize: 7, color: '#666', marginBottom: 6 }}>
                                Primary: reads billablePrices. Fallback: recalculates from raw node totals (exposes margin).
                            </Text>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontWeight: 'bold', fontSize: 7 }]}>Bucket</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontWeight: 'bold', fontSize: 7 }]}>Primary (billablePrices exists)</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>design</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>billablePrices.design  (stored, no recalc)</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>timberKit</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>billablePrices.timberKit  (stored, no recalc)</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>install</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>billablePrices.install + billablePrices.travel  ← travel merged for display</Text>
                            </View>
                            <View style={[styles.nodeRow, { backgroundColor: '#f5f5f5' }]}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontWeight: 'bold', fontSize: 7 }]}>total</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontWeight: 'bold', fontSize: 6.5 }]}>billablePrices.subtotal</Text>
                            </View>
                            <Text style={{ fontSize: 6.5, color: '#999', marginTop: 4 }}>
                                Fallback: design=round(design.total/…×qty), timberKit=round((mat+prod+frt+oh)/…×qty), install=round((inst+travel+ohTotal)/…×qty)
                            </Text>
                        </View>

                        {/* PATH 3: PDF DOWNLOAD (Custom) */}
                        <View style={[styles.structureBox, { marginBottom: 8 }]}>
                            <Text style={[styles.structureTitle, { marginBottom: 2 }]}>③ PDF Download — calculateBuckets() → grandTotals</Text>
                            <Text style={{ fontSize: 7, color: '#666', marginBottom: 6 }}>
                                Always recalculates from raw node totals. Never checks billablePrices for custom proposals.
                            </Text>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontWeight: 'bold', fontSize: 7 }]}>Bucket</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontWeight: 'bold', fontSize: 7 }]}>Formula (always recalculated)</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>design</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>round( design.total / (1 - margin/100) × qty )</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>timberKit</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>round( (materials + production + freight + overhead) / (1 - margin/100) × qty )</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>install</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>round( (install.total + travel.total + overheadTotal) / (1 - margin/100) × qty )  ⚠ travel combined</Text>
                            </View>
                            <View style={[styles.nodeRow, { backgroundColor: '#f5f5f5' }]}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontWeight: 'bold', fontSize: 7 }]}>total</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontWeight: 'bold', fontSize: 6.5 }]}>design + timberKit + install</Text>
                            </View>
                        </View>
                    </>
                ) : (
                    <>
                        {/* ============ KIT PROPOSAL PATHS ============ */}

                        {/* PATH 1: SAVE-TIME (Kit) */}
                        <View style={[styles.structureBox, { marginBottom: 8 }]}>
                            <Text style={[styles.structureTitle, { marginBottom: 2 }]}>① Save-Time — handleSaveProposal()</Text>
                            <Text style={{ fontSize: 7, color: '#666', marginBottom: 6 }}>
                                billablePrices are still saved per-nodeSet (same formulas as custom), BUT grand total uses kitPricing instead.
                                displayTotal = kitPricing[activeSpecies].totalCost + applyProfitMargin(Σ(install.total + travel.total) × qty).
                            </Text>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontWeight: 'bold', fontSize: 7 }]}>Bucket</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontWeight: 'bold', fontSize: 7 }]}>Grand Total Formula (displayTotal)</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>design</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>0  (included in kit price)</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>timberKit</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>kitPricing.find(kp → kp.speciesName === activeSpecies).totalCost  ← pre-computed by KitBuilder</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>install</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>round( Σ(install.total + travel.total) × qty / (1 - margin/100) )  ← raw recalc, profit applied once</Text>
                            </View>
                            <View style={[styles.nodeRow, { backgroundColor: '#f5f5f5' }]}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontWeight: 'bold', fontSize: 7 }]}>total</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontWeight: 'bold', fontSize: 6.5 }]}>kitBillable + installBillable  (if install not optional)</Text>
                            </View>
                        </View>

                        {/* PATH 2: PUBLIC PAGE (Kit) */}
                        <View style={[styles.structureBox, { marginBottom: 8 }]}>
                            <Text style={[styles.structureTitle, { marginBottom: 2 }]}>② Public Page — grandTotals useMemo (kit branch)</Text>
                            <Text style={{ fontSize: 7, color: '#666', marginBottom: 6 }}>
                                Bypasses calculateBuckets entirely. Uses kitPricing for design+timberKit, raw nodes for install.
                            </Text>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontWeight: 'bold', fontSize: 7 }]}>Field</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontWeight: 'bold', fontSize: 7 }]}>Single-Structure Kit</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>timberKit</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>kitPricing.find(activeSpecies).totalCost  ← already includes profit from KitBuilder</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>install</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>round( Σ(install.total + travel.total) × qty / (1 - margin/100) )</Text>
                            </View>
                            <View style={[styles.nodeRow, { backgroundColor: '#f5f5f5' }]}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontWeight: 'bold', fontSize: 7 }]}>total</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontWeight: 'bold', fontSize: 6.5 }]}>kitBillable + installAmount  (design=0, travel=0)</Text>
                            </View>

                            <Text style={{ fontSize: 7, color: '#666', marginTop: 6, marginBottom: 4 }}>
                                Multi-structure kit (nodeSets {'>'} 1) — timberKit per structure:
                            </Text>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>Primary</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>kitPricing.structurePricing[nsIndex].cost  ← per-structure cost from KitBuilder</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>Fallback</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>kitPricing.totalCost / nodeSets.length  ← equal divide</Text>
                            </View>
                        </View>

                        {/* PATH 3: PDF DOWNLOAD (Kit) */}
                        <View style={[styles.structureBox, { marginBottom: 8 }]}>
                            <Text style={[styles.structureTitle, { marginBottom: 2 }]}>③ PDF Download — grandTotals IIFE (kit branch)</Text>
                            <Text style={{ fontSize: 7, color: '#666', marginBottom: 6 }}>
                                Similar to public page but has MORE fallback levels for multi-structure kits.
                            </Text>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontWeight: 'bold', fontSize: 7 }]}>Field</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontWeight: 'bold', fontSize: 7 }]}>Single-Structure Kit</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>timberKit</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>kitPricing.find(activeSpecies).totalCost  ← same as public page</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>install</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>round( Σ(install.total + travel.total) × qty / (1 - margin/100) )</Text>
                            </View>

                            <Text style={{ fontSize: 7, color: '#c62828', marginTop: 6, marginBottom: 4, fontWeight: 'bold' }}>
                                ⚠ Multi-structure kit — PDF has 4-level fallback (vs public page's 2-level):
                            </Text>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>Level 1</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>kitPricing.structurePricing[nsIndex].cost  ← per-structure</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>Level 2</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>ns.billablePrices.speciesOptions[species]  ← NOT in public page</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>Level 3</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>ns.billablePrices.timberKit  ← NOT in public page</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '22%', fontSize: 7 }]}>Level 4</Text>
                                <Text style={[styles.nodeValue, { width: '78%', fontSize: 6.5 }]}>kitPricing.totalCost / nodeSets.length  ← equal divide (same as public fallback)</Text>
                            </View>
                        </View>
                    </>
                )}

                <Text style={styles.footer}>INTERNAL DEBUG REPORT - NOT FOR DISTRIBUTION</Text>
            </Page>

            {/* PAGE 6: KNOWN DISCREPANCIES + NODE TOTAL FORMULAS */}
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <Text style={styles.title}>Discrepancies & Node Formulas</Text>
                    <Text style={styles.subtitle}>Known differences between calculation paths, plus how each node.total is computed</Text>
                </View>

                {/* DISCREPANCIES */}
                <View style={[styles.structureBox, { marginBottom: 10 }]}>
                    <Text style={[styles.structureTitle, { marginBottom: 4, color: '#c62828' }]}>⚠ Known Discrepancies — {debugData.proposalType === 'kit' ? 'KIT' : 'CUSTOM'} Proposals</Text>

                    {debugData.proposalType === 'custom' ? (
                        <>
                            <View style={[styles.nodeRow, { backgroundColor: '#fff3e0' }]}>
                                <Text style={[styles.nodeLabel, { width: '25%', fontWeight: 'bold', fontSize: 7 }]}>install.overheadTotal</Text>
                                <Text style={[styles.nodeValue, { width: '75%', fontSize: 7 }]}>
                                    Save-time, public-page fallback, and PDF all add (install.overheadTotal || 0) to install.total.
                                    But install.total already includes overhead (via recalculateInstallTotal). Currently masked because
                                    overheadTotal is always 0 on saved nodes. If overheadTotal were ever set, it would double-count.
                                </Text>
                            </View>

                            <View style={[styles.nodeRow, { backgroundColor: '#fff3e0' }]}>
                                <Text style={[styles.nodeLabel, { width: '25%', fontWeight: 'bold', fontSize: 7 }]}>Travel handling</Text>
                                <Text style={[styles.nodeValue, { width: '75%', fontSize: 7 }]}>
                                    Save-time: install and travel are separate billablePrices fields.{'\n'}
                                    PDF + public-page fallback: travel.total is added INTO install bucket.{'\n'}
                                    Rounding may differ: round(install + travel) ≠ round(install) + round(travel).
                                </Text>
                            </View>

                            <View style={[styles.nodeRow, { backgroundColor: '#fff3e0' }]}>
                                <Text style={[styles.nodeLabel, { width: '25%', fontWeight: 'bold', fontSize: 7 }]}>PDF vs Public Page</Text>
                                <Text style={[styles.nodeValue, { width: '75%', fontSize: 7 }]}>
                                    Public page uses stored billablePrices (computed at save-time).{'\n'}
                                    PDF always recalculates from raw node.total values.{'\n'}
                                    If node totals change after last save, PDF shows new values while public page shows old ones.
                                </Text>
                            </View>
                        </>
                    ) : (
                        <>
                            <View style={[styles.nodeRow, { backgroundColor: '#fff3e0' }]}>
                                <Text style={[styles.nodeLabel, { width: '25%', fontWeight: 'bold', fontSize: 7 }]}>Multi-struct fallback</Text>
                                <Text style={[styles.nodeValue, { width: '75%', fontSize: 7 }]}>
                                    PDF has 4-level fallback chain for multi-structure kit timberKit:{'\n'}
                                    structurePricing → speciesOptions → billablePrices.timberKit → equal divide.{'\n'}
                                    Public page only has 2: structurePricing → equal divide.{'\n'}
                                    This means levels 2-3 can produce different timberKit values between PDF and public page.
                                </Text>
                            </View>

                            <View style={[styles.nodeRow, { backgroundColor: '#fff3e0' }]}>
                                <Text style={[styles.nodeLabel, { width: '25%', fontWeight: 'bold', fontSize: 7 }]}>Orphaned billablePrices</Text>
                                <Text style={[styles.nodeValue, { width: '75%', fontSize: 7 }]}>
                                    billablePrices are still computed and saved per-nodeSet at save-time (same formulas as custom).{'\n'}
                                    BUT both public page and PDF IGNORE them for kit proposals — grandTotals only uses kitPricing.{'\n'}
                                    These stored billablePrices are misleading in the debug data when viewing kit proposals.
                                </Text>
                            </View>

                            <View style={[styles.nodeRow, { backgroundColor: '#fff3e0' }]}>
                                <Text style={[styles.nodeLabel, { width: '25%', fontWeight: 'bold', fontSize: 7 }]}>Margin source mismatch</Text>
                                <Text style={[styles.nodeValue, { width: '75%', fontSize: 7 }]}>
                                    Kit timberKit price comes pre-margined from KitBuilder (margin baked into kitPricing.totalCost).{'\n'}
                                    Install still uses applyProfitMargin(rawCost, proposal.profitMargin) at display time.{'\n'}
                                    If kit margin ≠ proposal margin, total is a mix of two different profit margins.
                                </Text>
                            </View>

                            <View style={[styles.nodeRow, { backgroundColor: '#fff3e0' }]}>
                                <Text style={[styles.nodeLabel, { width: '25%', fontWeight: 'bold', fontSize: 7 }]}>install.overheadTotal</Text>
                                <Text style={[styles.nodeValue, { width: '75%', fontSize: 7 }]}>
                                    Kit install formula does NOT include install.overheadTotal (unlike custom).{'\n'}
                                    Kit: Σ(install.total + travel.total) × qty. Custom: install.total + overheadTotal.{'\n'}
                                    If a kit proposal ever had overheadTotal set, it would be silently dropped.
                                </Text>
                            </View>
                        </>
                    )}
                </View>

                {/* NODE TOTAL FORMULAS */}
                <View style={[styles.structureBox, { marginBottom: 8 }]}>
                    <Text style={[styles.structureTitle, { marginBottom: 4 }]}>Node Total Formulas — StructureDetailModal.tsx</Text>
                    <Text style={{ fontSize: 7, color: '#666', marginBottom: 6 }}>
                        These recalculate functions produce each node's .total value. All downstream paths use .total.
                    </Text>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '18%', fontWeight: 'bold', fontSize: 7 }]}>Node</Text>
                        <Text style={[styles.nodeLabel, { width: '30%', fontWeight: 'bold', fontSize: 7 }]}>Function</Text>
                        <Text style={[styles.nodeValue, { width: '52%', fontWeight: 'bold', fontSize: 7 }]}>Formula</Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '18%', fontSize: 7 }]}>Design</Text>
                        <Text style={[styles.nodeLabel, { width: '30%', fontSize: 6.5 }]}>recalculateDesignTotal()</Text>
                        <Text style={[styles.nodeValue, { width: '52%', fontSize: 6.5 }]}>pm×pmRate + lead×leadRate + support×supportRate + eng×engRate</Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '18%', fontSize: 7 }]}>Production</Text>
                        <Text style={[styles.nodeLabel, { width: '30%', fontSize: 6.5 }]}>inline calculation</Text>
                        <Text style={[styles.nodeValue, { width: '52%', fontSize: 6.5 }]}>boardFeet × productionRatePerBdft</Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '18%', fontSize: 7 }]}>Materials</Text>
                        <Text style={[styles.nodeLabel, { width: '30%', fontSize: 6.5 }]}>MaterialsInputs calculateTotals()</Text>
                        <Text style={[styles.nodeValue, { width: '52%', fontSize: 6.5 }]}>timberTotal + pegsCost + postBasesTotal + fastenerPackageCost</Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '18%', fontSize: 7 }]}>Freight</Text>
                        <Text style={[styles.nodeLabel, { width: '30%', fontSize: 6.5 }]}>inline calculation</Text>
                        <Text style={[styles.nodeValue, { width: '52%', fontSize: 6.5 }]}>Σ legs: (miles × costPerMile + flatFee)</Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '18%', fontSize: 7 }]}>Overhead</Text>
                        <Text style={[styles.nodeLabel, { width: '30%', fontSize: 6.5 }]}>inline calculation</Text>
                        <Text style={[styles.nodeValue, { width: '52%', fontSize: 6.5 }]}>days × overheadDailyRate  (from ProposalRateSettings)</Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '18%', fontSize: 7 }]}>Install</Text>
                        <Text style={[styles.nodeLabel, { width: '30%', fontSize: 6.5 }]}>recalculateInstallTotal()</Text>
                        <Text style={[styles.nodeValue, { width: '52%', fontSize: 6.5 }]}>days × (teamLaborPerDay + overheadDailyRate) + stacked equipment lines (or legacy day rates)</Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '18%', fontSize: 7 }]}>Travel</Text>
                        <Text style={[styles.nodeLabel, { width: '30%', fontSize: 6.5 }]}>recalculateTravelTotal()</Text>
                        <Text style={[styles.nodeValue, { width: '52%', fontSize: 6.5 }]}>hotelNights × hotelRate + crewSize × perDiem × (hotelNights + 1) + fuelBudget</Text>
                    </View>
                </View>

                {/* BUCKET GROUPING */}
                <View style={[styles.structureBox, { marginBottom: 8 }]}>
                    <Text style={[styles.structureTitle, { marginBottom: 4 }]}>Bucket Grouping — {debugData.proposalType === 'kit' ? 'KIT' : 'CUSTOM'}</Text>
                    <Text style={{ fontSize: 7, color: '#666', marginBottom: 6 }}>
                        {debugData.proposalType === 'custom'
                            ? 'How the 7 node totals are grouped into the 3 billable buckets shown to customers (via calculateTotals).'
                            : 'Kit proposals use only 2 effective buckets. Design is absorbed into kitPricing — it is NOT a separate bucket.'}
                    </Text>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '25%', fontWeight: 'bold', fontSize: 7 }]}>Bucket</Text>
                        <Text style={[styles.nodeValue, { width: '75%', fontWeight: 'bold', fontSize: 7 }]}>
                            {debugData.proposalType === 'custom' ? 'Composed of (raw node totals × qty, summed across structures)' : 'Source'}
                        </Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '25%', fontSize: 7 }]}>Design</Text>
                        <Text style={[styles.nodeValue, { width: '75%', fontSize: 7 }]}>
                            {debugData.proposalType === 'custom' ? 'design.total × qty' : '0 — absorbed into Kit Price (kitPricing.totalCost includes design costs)'}
                        </Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '25%', fontSize: 7 }]}>Timber Kit</Text>
                        <Text style={[styles.nodeValue, { width: '75%', fontSize: 7 }]}>
                            {debugData.proposalType === 'custom'
                                ? '(production.total + materials.total + freight.total + overhead.total) × qty'
                                : 'kitPricing[activeSpecies].totalCost — pre-computed by KitBuilder, includes design + materials + production + freight + overhead + profit'}
                        </Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '25%', fontSize: 7 }]}>Install</Text>
                        <Text style={[styles.nodeValue, { width: '75%', fontSize: 7 }]}>
                            {debugData.proposalType === 'custom'
                                ? '(install.total + travel.total) × qty    — note: overhead.total is in Timber Kit, not Install'
                                : 'Σ(install.total + travel.total) × qty — recalculated from raw nodes, profit margin applied via applyProfitMargin()'}
                        </Text>
                    </View>
                    <View style={[styles.nodeRow, { backgroundColor: '#f5f5f5' }]}>
                        <Text style={[styles.nodeLabel, { width: '25%', fontWeight: 'bold', fontSize: 7 }]}>Grand Total</Text>
                        <Text style={[styles.nodeValue, { width: '75%', fontWeight: 'bold', fontSize: 7 }]}>
                            {debugData.proposalType === 'custom'
                                ? 'Design + Timber Kit + Install, then applyProfitMargin() = cost / (1 - margin%/100)'
                                : 'kitBillable + installBillable — margin already in kit price, applied separately to install'}
                        </Text>
                    </View>
                </View>

                {/* LIVE COMPARISON */}
                {debugData.proposalType === 'kit' ? (
                    <>
                        {/* KIT: Section 1 — What The Page Shows */}
                        <View style={[styles.structureBox, { marginBottom: 8 }]}>
                            <Text style={[styles.structureTitle, { marginBottom: 4 }]}>What The Page Shows (Kit Calculation Path)</Text>
                            <Text style={{ fontSize: 7, color: '#666', marginBottom: 6 }}>
                                TimberKit from kitPricing.structurePricing (active species: {debugData.activeSpecies || 'N/A'}).
                                Install recalculated from raw nodes. Design = $0 (absorbed into kit price).
                            </Text>
                            {(() => {
                                const activeKitPricing = debugData.kitPricing
                                    ? debugData.kitPricing.find(kp => kp.speciesName === debugData.activeSpecies) || debugData.kitPricing[0]
                                    : undefined;
                                const isMultiStructure = debugData.structures.length > 1;
                                let grandTK = 0;
                                let grandInstall = 0;

                                const rows = debugData.structures.map((s, i) => {
                                    const qty = s.quantity;
                                    const margin = debugData.profitMarginPercent;

                                    // TimberKit from kitPricing
                                    let tk: number;
                                    if (activeKitPricing) {
                                        if (isMultiStructure && activeKitPricing.structurePricing?.[i]) {
                                            tk = Math.round(activeKitPricing.structurePricing[i].cost || 0);
                                        } else if (isMultiStructure) {
                                            tk = Math.round((activeKitPricing.totalCost || 0) / debugData.structures.length);
                                        } else {
                                            tk = Math.round(activeKitPricing.totalCost || 0);
                                        }
                                    } else {
                                        tk = 0;
                                    }

                                    // Install recalculated from raw nodes
                                    const install = Math.round(applyMargin(
                                        (s.installRaw + s.travelRaw) * qty, margin
                                    ));

                                    grandTK += tk;
                                    grandInstall += install;
                                    const structureTotal = tk + install;

                                    return (
                                        <View key={s.id} style={{ marginBottom: 2 }}>
                                            <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#333', marginBottom: 1 }}>
                                                {s.name}{qty > 1 ? ` (×${qty})` : ''}
                                            </Text>
                                            <View style={styles.nodeRow}>
                                                <Text style={[styles.nodeLabel, { width: '40%', fontSize: 7 }]}>  Timber Kit (from kitPricing)</Text>
                                                <Text style={[styles.nodeValue, { width: '60%', fontSize: 7 }]}>{fmt(tk)}</Text>
                                            </View>
                                            <View style={styles.nodeRow}>
                                                <Text style={[styles.nodeLabel, { width: '40%', fontSize: 7 }]}>  Install+Travel (recalculated)</Text>
                                                <Text style={[styles.nodeValue, { width: '60%', fontSize: 7 }]}>{fmt(install)}</Text>
                                            </View>
                                            <View style={[styles.nodeRow, { backgroundColor: '#f5f5f5' }]}>
                                                <Text style={[styles.nodeLabel, { width: '40%', fontWeight: 'bold', fontSize: 7 }]}>  Structure Total</Text>
                                                <Text style={[styles.nodeValue, { width: '60%', fontWeight: 'bold', fontSize: 7 }]}>{fmt(structureTotal)}</Text>
                                            </View>
                                        </View>
                                    );
                                });

                                const pageGrandTotal = grandTK + grandInstall;

                                return (
                                    <>
                                        {rows}
                                        <View style={[styles.nodeRow, { backgroundColor: '#e8f5e9', marginTop: 4 }]}>
                                            <Text style={[styles.nodeLabel, { width: '25%', fontWeight: 'bold', fontSize: 7.5 }]}>Page Grand Total</Text>
                                            <Text style={[styles.nodeValue, { width: '25%', fontWeight: 'bold', fontSize: 7 }]}>TK: {fmt(grandTK)}</Text>
                                            <Text style={[styles.nodeValue, { width: '25%', fontWeight: 'bold', fontSize: 7 }]}>Install: {fmt(grandInstall)}</Text>
                                            <Text style={[styles.nodeValue, { width: '25%', fontWeight: 'bold', fontSize: 7.5 }]}>{fmt(pageGrandTotal)}</Text>
                                        </View>
                                    </>
                                );
                            })()}
                        </View>

                        {/* KIT: Section 2 — Grand Total Sanity Check */}
                        <View style={[styles.structureBox, { marginBottom: 8 }]}>
                            <Text style={[styles.structureTitle, { marginBottom: 4 }]}>Grand Total Sanity Check</Text>
                            <Text style={{ fontSize: 7, color: '#666', marginBottom: 6 }}>
                                Compares stored displayTotal (what deal cards show) with the recalculated page total.
                                A mismatch here means the deal card shows a stale or incorrect total.
                            </Text>
                            {(() => {
                                const activeKitPricing = debugData.kitPricing
                                    ? debugData.kitPricing.find(kp => kp.speciesName === debugData.activeSpecies) || debugData.kitPricing[0]
                                    : undefined;
                                const isMultiStructure = debugData.structures.length > 1;

                                let recalcTK = 0;
                                let recalcInstall = 0;
                                debugData.structures.forEach((s, i) => {
                                    const qty = s.quantity;
                                    const margin = debugData.profitMarginPercent;
                                    if (activeKitPricing) {
                                        if (isMultiStructure && activeKitPricing.structurePricing?.[i]) {
                                            recalcTK += Math.round(activeKitPricing.structurePricing[i].cost || 0);
                                        } else if (isMultiStructure) {
                                            recalcTK += Math.round((activeKitPricing.totalCost || 0) / debugData.structures.length);
                                        } else {
                                            recalcTK += Math.round(activeKitPricing.totalCost || 0);
                                        }
                                    }
                                    recalcInstall += Math.round(applyMargin(
                                        (s.installRaw + s.travelRaw) * qty, margin
                                    ));
                                });
                                const recalcTotal = recalcTK + recalcInstall;
                                const displayTotal = debugData.displayTotal;
                                const storedBillable = debugData.storedBillableTotal;
                                const displayMatch = displayTotal != null ? displayTotal === recalcTotal : undefined;
                                const billableMatch = storedBillable != null ? storedBillable === recalcTotal : undefined;

                                return (
                                    <>
                                        <View style={styles.nodeRow}>
                                            <Text style={[styles.nodeLabel, { width: '45%', fontWeight: 'bold', fontSize: 7 }]}></Text>
                                            <Text style={[styles.nodeValue, { width: '20%', fontWeight: 'bold', fontSize: 7 }]}>Value</Text>
                                            <Text style={[styles.nodeValue, { width: '35%', fontWeight: 'bold', fontSize: 7 }]}>Status</Text>
                                        </View>
                                        <View style={styles.nodeRow}>
                                            <Text style={[styles.nodeLabel, { width: '45%', fontSize: 7 }]}>Recalculated (kitPricing + install)</Text>
                                            <Text style={[styles.nodeValue, { width: '20%', fontWeight: 'bold', fontSize: 7 }]}>{fmt(recalcTotal)}</Text>
                                            <Text style={[styles.nodeValue, { width: '35%', fontSize: 7, color: '#666' }]}>baseline</Text>
                                        </View>
                                        <View style={[styles.nodeRow, { backgroundColor: displayMatch === false ? '#ffebee' : displayMatch === true ? '#e8f5e9' : '#fff' }]}>
                                            <Text style={[styles.nodeLabel, { width: '45%', fontSize: 7 }]}>displayTotal (Firestore)</Text>
                                            <Text style={[styles.nodeValue, { width: '20%', fontSize: 7 }]}>{displayTotal != null ? fmt(displayTotal) : 'N/A'}</Text>
                                            <Text style={[styles.nodeValue, { width: '35%', fontSize: 7, fontWeight: 'bold', color: displayMatch === false ? '#c62828' : displayMatch === true ? '#2e7d32' : '#999' }]}>
                                                {displayMatch === true ? '✓ MATCH' : displayMatch === false ? `${fmt(displayTotal! - recalcTotal)} MISMATCH` : 'not available'}
                                            </Text>
                                        </View>
                                        <View style={[styles.nodeRow, { backgroundColor: billableMatch === false ? '#fff8e1' : billableMatch === true ? '#e8f5e9' : '#fff' }]}>
                                            <Text style={[styles.nodeLabel, { width: '45%', fontSize: 7 }]}>billableTotal (Firestore)</Text>
                                            <Text style={[styles.nodeValue, { width: '20%', fontSize: 7 }]}>{storedBillable != null ? fmt(storedBillable) : 'N/A'}</Text>
                                            <Text style={[styles.nodeValue, { width: '35%', fontSize: 7, color: billableMatch === false ? '#e65100' : billableMatch === true ? '#2e7d32' : '#999' }]}>
                                                {billableMatch === true ? '✓ MATCH' : billableMatch === false ? `${fmt(storedBillable! - recalcTotal)} (uses custom formula at save)` : 'not available'}
                                            </Text>
                                        </View>
                                    </>
                                );
                            })()}
                        </View>

                        {/* KIT: Section 3 — Stored billablePrices Reference */}
                        <View style={[styles.structureBox, { marginBottom: 8 }]}>
                            <Text style={[styles.structureTitle, { marginBottom: 4, color: '#999' }]}>Stored billablePrices Reference (not used for kit display)</Text>
                            <Text style={{ fontSize: 6.5, color: '#999', marginBottom: 4 }}>
                                These values were computed at save-time using the custom formula (design + TK + install + travel).
                                Kit proposals don't use these for display — they use kitPricing + raw install recalc instead.
                            </Text>
                            {debugData.storedBillablePrices.map((sp, i) => (
                                <View key={sp.structureId} style={{ marginBottom: i < debugData.storedBillablePrices.length - 1 ? 3 : 0 }}>
                                    <Text style={{ fontSize: 7, color: '#999', fontWeight: 'bold' }}>{sp.structureName}</Text>
                                    <Text style={{ fontSize: 6.5, color: '#aaa' }}>
                                        {'  '}Design: {fmt(sp.design)}  |  TK: {fmt(sp.timberKit)}  |  Install: {fmt(sp.install)}  |  Travel: {fmt(sp.travel)}  |  Subtotal: {fmt(sp.subtotal)}
                                    </Text>
                                </View>
                            ))}
                        </View>
                    </>
                ) : (
                    /* CUSTOM: Keep existing stored vs recalc comparison */
                    <View style={[styles.structureBox, { marginBottom: 8 }]}>
                        <Text style={[styles.structureTitle, { marginBottom: 4 }]}>Live Comparison: Stored billablePrices vs Recalculated</Text>
                        <Text style={{ fontSize: 7, color: '#666', marginBottom: 6 }}>
                            If these differ, the public page and PDF will show different numbers.
                        </Text>
                        {debugData.structures.map((s, i) => {
                            const stored = debugData.storedBillablePrices.find(sp => sp.structureId === s.id);
                            const qty = s.quantity;
                            const margin = debugData.profitMarginPercent;
                            const recalcDesign = Math.round(applyMargin(s.designRaw, margin) * qty);
                            const recalcTimberKit = Math.round(applyMargin(
                                s.productionRaw + s.materialsRaw + s.freightRaw + s.overheadRaw, margin
                            ) * qty);
                            const recalcInstall = Math.round(applyMargin(
                                s.installRaw + s.travelRaw, margin
                            ) * qty);
                            const recalcTotal = recalcDesign + recalcTimberKit + recalcInstall;
                            const storedTotal = stored ? stored.subtotal : 0;
                            const match = stored ? storedTotal === recalcTotal : false;
                            const diffAmount = stored ? recalcTotal - storedTotal : 0;

                            return (
                                <View key={s.id} style={{ marginBottom: i < debugData.structures.length - 1 ? 6 : 0 }}>
                                    <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#333', marginBottom: 2 }}>{s.name}</Text>
                                    <View style={styles.nodeRow}>
                                        <Text style={[styles.nodeLabel, { width: '30%', fontWeight: 'bold', fontSize: 7 }]}></Text>
                                        <Text style={[styles.nodeValue, { width: '23%', fontWeight: 'bold', fontSize: 7 }]}>Stored (Page)</Text>
                                        <Text style={[styles.nodeValue, { width: '23%', fontWeight: 'bold', fontSize: 7 }]}>Recalc (PDF)</Text>
                                        <Text style={[styles.nodeValue, { width: '24%', fontWeight: 'bold', fontSize: 7 }]}>Diff</Text>
                                    </View>
                                    <View style={styles.nodeRow}>
                                        <Text style={[styles.nodeLabel, { width: '30%', fontSize: 7 }]}>Design</Text>
                                        <Text style={[styles.nodeValue, { width: '23%', fontSize: 7 }]}>{stored ? fmt(stored.design) : 'N/A'}</Text>
                                        <Text style={[styles.nodeValue, { width: '23%', fontSize: 7 }]}>{fmt(recalcDesign)}</Text>
                                        <Text style={[styles.nodeValue, { width: '24%', fontSize: 7, color: stored && stored.design !== recalcDesign ? '#c62828' : '#2e7d32' }]}>
                                            {stored ? (stored.design === recalcDesign ? '✓ match' : fmt(recalcDesign - stored.design)) : '—'}
                                        </Text>
                                    </View>
                                    <View style={styles.nodeRow}>
                                        <Text style={[styles.nodeLabel, { width: '30%', fontSize: 7 }]}>Timber Kit</Text>
                                        <Text style={[styles.nodeValue, { width: '23%', fontSize: 7 }]}>{stored ? fmt(stored.timberKit) : 'N/A'}</Text>
                                        <Text style={[styles.nodeValue, { width: '23%', fontSize: 7 }]}>{fmt(recalcTimberKit)}</Text>
                                        <Text style={[styles.nodeValue, { width: '24%', fontSize: 7, color: stored && stored.timberKit !== recalcTimberKit ? '#c62828' : '#2e7d32' }]}>
                                            {stored ? (stored.timberKit === recalcTimberKit ? '✓ match' : fmt(recalcTimberKit - stored.timberKit)) : '—'}
                                        </Text>
                                    </View>
                                    <View style={styles.nodeRow}>
                                        <Text style={[styles.nodeLabel, { width: '30%', fontSize: 7 }]}>Install+Travel</Text>
                                        <Text style={[styles.nodeValue, { width: '23%', fontSize: 7 }]}>{stored ? fmt(stored.install + stored.travel) : 'N/A'}</Text>
                                        <Text style={[styles.nodeValue, { width: '23%', fontSize: 7 }]}>{fmt(recalcInstall)}</Text>
                                        <Text style={[styles.nodeValue, { width: '24%', fontSize: 7, color: stored && (stored.install + stored.travel) !== recalcInstall ? '#c62828' : '#2e7d32' }]}>
                                            {stored ? ((stored.install + stored.travel) === recalcInstall ? '✓ match' : fmt(recalcInstall - (stored.install + stored.travel))) : '—'}
                                        </Text>
                                    </View>
                                    <View style={[styles.nodeRow, { backgroundColor: match ? '#e8f5e9' : '#ffebee' }]}>
                                        <Text style={[styles.nodeLabel, { width: '30%', fontWeight: 'bold', fontSize: 7 }]}>Total</Text>
                                        <Text style={[styles.nodeValue, { width: '23%', fontWeight: 'bold', fontSize: 7 }]}>{stored ? fmt(storedTotal) : 'N/A'}</Text>
                                        <Text style={[styles.nodeValue, { width: '23%', fontWeight: 'bold', fontSize: 7 }]}>{fmt(recalcTotal)}</Text>
                                        <Text style={[styles.nodeValue, { width: '24%', fontWeight: 'bold', fontSize: 7, color: match ? '#2e7d32' : '#c62828' }]}>
                                            {stored ? (match ? '✓ MATCH' : `${fmt(diffAmount)} MISMATCH`) : '—'}
                                        </Text>
                                    </View>
                                </View>
                            );
                        })}
                    </View>
                )}

                <Text style={styles.footer}>INTERNAL DEBUG REPORT - NOT FOR DISTRIBUTION</Text>
            </Page>

            {/* PAGE 7: PROPOSAL HEALTH DASHBOARD */}
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <Text style={styles.title}>Proposal Health Dashboard</Text>
                    <Text style={styles.subtitle}>Staleness, flags, total comparisons, and margin consistency</Text>
                </View>

                {/* STALENESS CHECK */}
                <View style={[styles.structureBox, { marginBottom: 10 }]}>
                    <Text style={[styles.structureTitle, { marginBottom: 4 }]}>📅 Data Staleness Check</Text>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '35%', fontWeight: 'bold', fontSize: 7 }]}>Field</Text>
                        <Text style={[styles.nodeValue, { width: '65%', fontWeight: 'bold', fontSize: 7 }]}>Value</Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '35%', fontSize: 7 }]}>Debug Generated At</Text>
                        <Text style={[styles.nodeValue, { width: '65%', fontSize: 7 }]}>{debugData.generatedAt}</Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '35%', fontSize: 7 }]}>Last Saved (updatedAt)</Text>
                        <Text style={[styles.nodeValue, { width: '65%', fontSize: 7 }]}>{debugData.updatedAt || 'NOT AVAILABLE'}</Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '35%', fontSize: 7 }]}>Created At</Text>
                        <Text style={[styles.nodeValue, { width: '65%', fontSize: 7 }]}>{debugData.createdAt || 'NOT AVAILABLE'}</Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '35%', fontSize: 7 }]}>Sent At</Text>
                        <Text style={[styles.nodeValue, { width: '65%', fontSize: 7 }]}>{debugData.sentAt || 'NOT SENT'}</Text>
                    </View>
                    {debugData.updatedAt && (() => {
                        const lastSave = new Date(debugData.updatedAt!).getTime();
                        const genTime = new Date(debugData.generatedAt).getTime();
                        const diffMs = genTime - lastSave;
                        const diffMins = Math.round(diffMs / 60000);
                        const isStale = diffMins > 60;
                        return (
                            <View style={[styles.nodeRow, { backgroundColor: isStale ? '#ffebee' : '#e8f5e9' }]}>
                                <Text style={[styles.nodeLabel, { width: '35%', fontWeight: 'bold', fontSize: 7 }]}>Time Since Last Save</Text>
                                <Text style={[styles.nodeValue, { width: '65%', fontWeight: 'bold', fontSize: 7, color: isStale ? '#c62828' : '#2e7d32' }]}>
                                    {diffMins < 1 ? '< 1 minute' : `${diffMins} minutes`}
                                    {isStale ? '  ⚠ STALE — billablePrices may not reflect current node totals' : '  ✓ FRESH'}
                                </Text>
                            </View>
                        );
                    })()}
                </View>

                {/* INSTALL OPTIONAL STATUS */}
                <View style={[styles.structureBox, { marginBottom: 10 }]}>
                    <Text style={[styles.structureTitle, { marginBottom: 4 }]}>🔧 Key Flags</Text>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '35%', fontWeight: 'bold', fontSize: 7 }]}>Flag</Text>
                        <Text style={[styles.nodeValue, { width: '30%', fontWeight: 'bold', fontSize: 7 }]}>Value</Text>
                        <Text style={[styles.nodeValue, { width: '35%', fontWeight: 'bold', fontSize: 7 }]}>Impact</Text>
                    </View>
                    <View style={[styles.nodeRow, { backgroundColor: debugData.installOptional ? '#fff3e0' : '#e8f5e9' }]}>
                        <Text style={[styles.nodeLabel, { width: '35%', fontSize: 7 }]}>installOptional</Text>
                        <Text style={[styles.nodeValue, { width: '30%', fontSize: 7, fontWeight: 'bold', color: debugData.installOptional ? '#e65100' : '#2e7d32' }]}>
                            {debugData.installOptional ? 'TRUE' : 'FALSE'}
                        </Text>
                        <Text style={[styles.nodeValue, { width: '35%', fontSize: 6.5 }]}>
                            {debugData.installOptional
                                ? 'Client can opt out — install bucket may drop to $0'
                                : 'Install always included in grand total'}
                        </Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '35%', fontSize: 7 }]}>pricingLocked</Text>
                        <Text style={[styles.nodeValue, { width: '30%', fontSize: 7, fontWeight: 'bold' }]}>
                            {debugData.pricingLocked ? 'TRUE — LOCKED' : 'FALSE'}
                        </Text>
                        <Text style={[styles.nodeValue, { width: '35%', fontSize: 6.5 }]}>
                            {debugData.pricingLocked
                                ? 'Material price changes will NOT update this proposal'
                                : 'Pricing may change if material rates update'}
                        </Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '35%', fontSize: 7 }]}>Proposal Status</Text>
                        <Text style={[styles.nodeValue, { width: '30%', fontSize: 7, fontWeight: 'bold' }]}>
                            {debugData.proposalStatus?.toUpperCase() || 'UNKNOWN'}
                        </Text>
                        <Text style={[styles.nodeValue, { width: '35%', fontSize: 6.5 }]}>
                            {debugData.proposalStatus === 'sent' ? 'Proposal is live — changes affect client view'
                                : debugData.proposalStatus === 'approved' ? 'Signed — should not be modified'
                                    : 'Draft — safe to edit'}
                        </Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '35%', fontSize: 7 }]}>Proposal Type</Text>
                        <Text style={[styles.nodeValue, { width: '30%', fontSize: 7, fontWeight: 'bold' }]}>
                            {debugData.proposalType.toUpperCase()}
                        </Text>
                        <Text style={[styles.nodeValue, { width: '35%', fontSize: 6.5 }]}>
                            {debugData.proposalType === 'kit'
                                ? `Source: ${debugData.sourceKitName || debugData.sourceKitId || 'unknown kit'}`
                                : 'Standard custom proposal'}
                        </Text>
                    </View>
                </View>

                {/* DISPLAY TOTAL COMPARISON */}
                <View style={[styles.structureBox, { marginBottom: 10 }]}>
                    <Text style={[styles.structureTitle, { marginBottom: 4 }]}>💰 Grand Total — 3-Way Comparison</Text>
                    <Text style={{ fontSize: 7, color: '#666', marginBottom: 6 }}>
                        Three sources of truth for the proposal grand total. If they disagree, something is off.
                    </Text>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '40%', fontWeight: 'bold', fontSize: 7 }]}>Source</Text>
                        <Text style={[styles.nodeValue, { width: '25%', fontWeight: 'bold', fontSize: 7 }]}>Value</Text>
                        <Text style={[styles.nodeValue, { width: '35%', fontWeight: 'bold', fontSize: 7 }]}>What Uses It</Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '40%', fontSize: 7 }]}>displayTotal (Firestore)</Text>
                        <Text style={[styles.nodeValue, { width: '25%', fontSize: 7, fontWeight: 'bold' }]}>
                            {debugData.displayTotal != null ? fmt(debugData.displayTotal) : 'NOT SET'}
                        </Text>
                        <Text style={[styles.nodeValue, { width: '35%', fontSize: 6.5 }]}>Deal cards, pipeline view</Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '40%', fontSize: 7 }]}>billableTotal (Firestore)</Text>
                        <Text style={[styles.nodeValue, { width: '25%', fontSize: 7, fontWeight: 'bold' }]}>
                            {debugData.storedBillableTotal != null ? fmt(debugData.storedBillableTotal) : 'NOT SET'}
                        </Text>
                        <Text style={[styles.nodeValue, { width: '35%', fontSize: 6.5 }]}>Summary line items</Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '40%', fontSize: 7 }]}>Recalculated (just now)</Text>
                        <Text style={[styles.nodeValue, { width: '25%', fontSize: 7, fontWeight: 'bold' }]}>
                            {fmt(debugData.recalculatedGrandTotal)}
                        </Text>
                        <Text style={[styles.nodeValue, { width: '35%', fontSize: 6.5 }]}>From raw node totals + margin</Text>
                    </View>
                    {/* Diff rows */}
                    {debugData.displayTotal != null && (
                        <View style={[styles.nodeRow, { backgroundColor: debugData.displayTotal === debugData.recalculatedGrandTotal ? '#e8f5e9' : '#ffebee' }]}>
                            <Text style={[styles.nodeLabel, { width: '40%', fontSize: 7, fontWeight: 'bold' }]}>displayTotal vs Recalculated</Text>
                            <Text style={[styles.nodeValue, { width: '25%', fontSize: 7, fontWeight: 'bold', color: debugData.displayTotal === debugData.recalculatedGrandTotal ? '#2e7d32' : '#c62828' }]}>
                                {debugData.displayTotal === debugData.recalculatedGrandTotal
                                    ? '✓ MATCH'
                                    : `${fmt(debugData.displayTotal - debugData.recalculatedGrandTotal)} diff`}
                            </Text>
                            <Text style={[styles.nodeValue, { width: '35%', fontSize: 6.5 }]}>
                                {debugData.proposalType === 'kit' ? 'Kit: expected diff if kit uses different calc' : ''}
                            </Text>
                        </View>
                    )}
                </View>

                {/* MARGIN CONSISTENCY CHECK */}
                <View style={[styles.structureBox, { marginBottom: 10 }]}>
                    <Text style={[styles.structureTitle, { marginBottom: 4 }]}>📊 Profit Margin Consistency</Text>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '40%', fontWeight: 'bold', fontSize: 7 }]}>Margin Source</Text>
                        <Text style={[styles.nodeValue, { width: '25%', fontWeight: 'bold', fontSize: 7 }]}>Value</Text>
                        <Text style={[styles.nodeValue, { width: '35%', fontWeight: 'bold', fontSize: 7 }]}>Used For</Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '40%', fontSize: 7 }]}>Proposal profitMargin</Text>
                        <Text style={[styles.nodeValue, { width: '25%', fontSize: 7, fontWeight: 'bold' }]}>{debugData.profitMarginPercent}%</Text>
                        <Text style={[styles.nodeValue, { width: '35%', fontSize: 6.5 }]}>Install bucket, custom billablePrices</Text>
                    </View>
                    {debugData.proposalType === 'kit' && (
                        <View style={[styles.nodeRow, {
                            backgroundColor: debugData.kitMarginMatchesProposal === false ? '#ffebee'
                                : debugData.kitMarginMatchesProposal === true ? '#e8f5e9' : undefined
                        }]}>
                            <Text style={[styles.nodeLabel, { width: '40%', fontSize: 7 }]}>Kit Builder Margin</Text>
                            <Text style={[styles.nodeValue, { width: '25%', fontSize: 7, fontWeight: 'bold', color: debugData.kitMarginMatchesProposal === false ? '#c62828' : undefined }]}>
                                {debugData.kitMarginPercent != null ? `${debugData.kitMarginPercent}%` : 'NOT TRACKED'}
                            </Text>
                            <Text style={[styles.nodeValue, { width: '35%', fontSize: 6.5 }]}>
                                {debugData.kitMarginMatchesProposal === false
                                    ? '⚠ MISMATCH — timberKit has different margin baked in!'
                                    : debugData.kitMarginMatchesProposal === true
                                        ? '✓ Matches proposal margin'
                                        : 'Kit margin not available for comparison'}
                            </Text>
                        </View>
                    )}
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '40%', fontSize: 7 }]}>Margin Multiplier</Text>
                        <Text style={[styles.nodeValue, { width: '25%', fontSize: 7 }]}>{debugData.marginMultiplier.toFixed(4)}×</Text>
                        <Text style={[styles.nodeValue, { width: '35%', fontSize: 6.5 }]}>cost × {debugData.marginMultiplier.toFixed(4)} = billable price</Text>
                    </View>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '40%', fontSize: 7 }]}>Stored rawCostTotal</Text>
                        <Text style={[styles.nodeValue, { width: '25%', fontSize: 7 }]}>
                            {debugData.storedRawCostTotal != null ? fmt(debugData.storedRawCostTotal) : 'NOT SET'}
                        </Text>
                        <Text style={[styles.nodeValue, { width: '35%', fontSize: 6.5 }]}>
                            vs recalculated: {fmt(debugData.buckets.grandTotalRaw)}
                            {debugData.storedRawCostTotal != null && debugData.storedRawCostTotal !== debugData.buckets.grandTotalRaw
                                ? ` (${fmt(debugData.storedRawCostTotal - debugData.buckets.grandTotalRaw)} diff)`
                                : ''}
                        </Text>
                    </View>
                </View>

                <Text style={styles.footer}>INTERNAL DEBUG REPORT - NOT FOR DISTRIBUTION</Text>
            </Page>

            {/* PAGE 8: SPECIES PRICING BREAKDOWN */}
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <Text style={styles.title}>Species Pricing Breakdown</Text>
                    <Text style={styles.subtitle}>
                        {debugData.proposalType === 'kit'
                            ? 'Kit pricing by species — from KitBuilder (pre-margined)'
                            : 'Per-structure species options from billablePrices'}
                    </Text>
                </View>

                {debugData.proposalType === 'kit' && debugData.kitPricing && debugData.kitPricing.length > 0 ? (
                    <>
                        {/* KIT SPECIES OVERVIEW */}
                        <View style={[styles.structureBox, { marginBottom: 10 }]}>
                            <Text style={[styles.structureTitle, { marginBottom: 4 }]}>Kit Pricing — All Species Options</Text>
                            <Text style={{ fontSize: 7, color: '#666', marginBottom: 6 }}>
                                Active species: {debugData.activeSpecies || 'not set'} | Offered: {(debugData.offeredSpecies || []).join(', ') || 'none'}
                            </Text>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '30%', fontWeight: 'bold', fontSize: 7 }]}>Species</Text>
                                <Text style={[styles.nodeValue, { width: '25%', fontWeight: 'bold', fontSize: 7 }]}>Total Cost</Text>
                                <Text style={[styles.nodeValue, { width: '20%', fontWeight: 'bold', fontSize: 7 }]}>Active?</Text>
                                <Text style={[styles.nodeValue, { width: '25%', fontWeight: 'bold', fontSize: 7 }]}>Offered?</Text>
                            </View>
                            {debugData.kitPricing.map((kp, i) => (
                                <View key={i} style={[styles.nodeRow, {
                                    backgroundColor: kp.speciesName === debugData.activeSpecies ? '#e3f2fd' : undefined
                                }]}>
                                    <Text style={[styles.nodeLabel, { width: '30%', fontSize: 7, fontWeight: kp.speciesName === debugData.activeSpecies ? 'bold' : undefined }]}>
                                        {kp.speciesName}
                                    </Text>
                                    <Text style={[styles.nodeValue, { width: '25%', fontSize: 7, fontWeight: 'bold' }]}>
                                        {fmt(kp.totalCost)}
                                    </Text>
                                    <Text style={[styles.nodeValue, { width: '20%', fontSize: 7 }]}>
                                        {kp.speciesName === debugData.activeSpecies ? '✓ ACTIVE' : ''}
                                    </Text>
                                    <Text style={[styles.nodeValue, { width: '25%', fontSize: 7 }]}>
                                        {(debugData.offeredSpecies || []).includes(kp.speciesName) ? '✓ YES' : 'NO'}
                                    </Text>
                                </View>
                            ))}
                        </View>

                        {/* PER-STRUCTURE KIT PRICING (multi-structure kits) */}
                        {debugData.kitPricing.some(kp => kp.structurePricing && kp.structurePricing.length > 0) && (
                            <View style={[styles.structureBox, { marginBottom: 10 }]}>
                                <Text style={[styles.structureTitle, { marginBottom: 4 }]}>Per-Structure Kit Pricing (Multi-Structure Kit)</Text>
                                <Text style={{ fontSize: 7, color: '#666', marginBottom: 6 }}>
                                    structurePricing breakdown — used as Level 1 fallback for multi-structure kits
                                </Text>
                                {debugData.kitPricing.filter(kp => kp.structurePricing).map((kp, ki) => (
                                    <View key={ki} style={{ marginBottom: ki < debugData.kitPricing!.length - 1 ? 6 : 0 }}>
                                        <Text style={{ fontSize: 7.5, fontWeight: 'bold', color: '#1565c0', marginBottom: 2 }}>
                                            {kp.speciesName} — Total: {fmt(kp.totalCost)}
                                        </Text>
                                        <View style={styles.nodeRow}>
                                            <Text style={[styles.nodeLabel, { width: '40%', fontWeight: 'bold', fontSize: 7 }]}>Structure</Text>
                                            <Text style={[styles.nodeValue, { width: '30%', fontWeight: 'bold', fontSize: 7 }]}>Cost</Text>
                                            <Text style={[styles.nodeValue, { width: '30%', fontWeight: 'bold', fontSize: 7 }]}>% of Total</Text>
                                        </View>
                                        {(kp.structurePricing || []).map((sp, si) => (
                                            <View key={si} style={styles.nodeRow}>
                                                <Text style={[styles.nodeLabel, { width: '40%', fontSize: 7 }]}>{sp.nodeSetName}</Text>
                                                <Text style={[styles.nodeValue, { width: '30%', fontSize: 7 }]}>{fmt(sp.cost)}</Text>
                                                <Text style={[styles.nodeValue, { width: '30%', fontSize: 7 }]}>
                                                    {kp.totalCost > 0 ? `${((sp.cost / kp.totalCost) * 100).toFixed(1)}%` : '—'}
                                                </Text>
                                            </View>
                                        ))}
                                    </View>
                                ))}
                            </View>
                        )}
                    </>
                ) : (
                    <>
                        {/* CUSTOM PROPOSAL SPECIES OPTIONS */}
                        <View style={[styles.structureBox, { marginBottom: 10 }]}>
                            <Text style={[styles.structureTitle, { marginBottom: 4 }]}>Per-Structure Species Options</Text>
                            <Text style={{ fontSize: 7, color: '#666', marginBottom: 6 }}>
                                From billablePrices.speciesOptions — timberKit price for each offered species
                            </Text>
                            {debugData.structures.map((s, si) => {
                                const stored = debugData.storedBillablePrices.find(sp => sp.structureId === s.id);
                                const speciesOpts = stored?.speciesOptions;
                                return (
                                    <View key={s.id} style={{ marginBottom: si < debugData.structures.length - 1 ? 6 : 0 }}>
                                        <Text style={{ fontSize: 7.5, fontWeight: 'bold', color: '#333', marginBottom: 2 }}>
                                            {s.name} (qty: {s.quantity})
                                        </Text>
                                        {speciesOpts && Object.keys(speciesOpts).length > 0 ? (
                                            <>
                                                <View style={styles.nodeRow}>
                                                    <Text style={[styles.nodeLabel, { width: '40%', fontWeight: 'bold', fontSize: 7 }]}>Species</Text>
                                                    <Text style={[styles.nodeValue, { width: '30%', fontWeight: 'bold', fontSize: 7 }]}>TimberKit Price</Text>
                                                    <Text style={[styles.nodeValue, { width: '30%', fontWeight: 'bold', fontSize: 7 }]}>vs Default</Text>
                                                </View>
                                                {Object.entries(speciesOpts).map(([species, price]) => (
                                                    <View key={species} style={[styles.nodeRow, {
                                                        backgroundColor: species === s.defaultSpeciesName ? '#e3f2fd' : undefined
                                                    }]}>
                                                        <Text style={[styles.nodeLabel, { width: '40%', fontSize: 7 }]}>
                                                            {species} {species === s.defaultSpeciesName ? '(default)' : ''}
                                                        </Text>
                                                        <Text style={[styles.nodeValue, { width: '30%', fontSize: 7 }]}>{fmt(price)}</Text>
                                                        <Text style={[styles.nodeValue, { width: '30%', fontSize: 7 }]}>
                                                            {stored && species !== s.defaultSpeciesName
                                                                ? fmt(price - stored.timberKit)
                                                                : '—'}
                                                        </Text>
                                                    </View>
                                                ))}
                                            </>
                                        ) : (
                                            <Text style={{ fontSize: 7, color: '#999' }}>No species options stored for this structure</Text>
                                        )}
                                    </View>
                                );
                            })}
                        </View>

                        {/* PER-STRUCTURE SPECIES FROM NODE DATA */}
                        <View style={[styles.structureBox, { marginBottom: 10 }]}>
                            <Text style={[styles.structureTitle, { marginBottom: 4 }]}>Species Pricing from Node Data</Text>
                            <Text style={{ fontSize: 7, color: '#666', marginBottom: 6 }}>
                                Raw recalculated species pricing per structure (from speciesPricing in extractStructureData)
                            </Text>
                            {debugData.structures.map((s, si) => (
                                <View key={s.id} style={{ marginBottom: si < debugData.structures.length - 1 ? 6 : 0 }}>
                                    <Text style={{ fontSize: 7.5, fontWeight: 'bold', color: '#333', marginBottom: 2 }}>
                                        {s.name} — Default: {s.defaultSpeciesName} | Board Feet: {s.totalBoardFeet.toLocaleString()}
                                    </Text>
                                    {s.speciesPricing.length > 0 ? (
                                        <>
                                            <View style={styles.nodeRow}>
                                                <Text style={[styles.nodeLabel, { width: '25%', fontWeight: 'bold', fontSize: 7 }]}>Species</Text>
                                                <Text style={[styles.nodeValue, { width: '20%', fontWeight: 'bold', fontSize: 7 }]}>Timber $</Text>
                                                <Text style={[styles.nodeValue, { width: '20%', fontWeight: 'bold', fontSize: 7 }]}>Materials $</Text>
                                                <Text style={[styles.nodeValue, { width: '20%', fontWeight: 'bold', fontSize: 7 }]}>TimberKit Raw</Text>
                                                <Text style={[styles.nodeValue, { width: '15%', fontWeight: 'bold', fontSize: 7 }]}>Default?</Text>
                                            </View>
                                            {s.speciesPricing.map((sp, spi) => (
                                                <View key={spi} style={[styles.nodeRow, { backgroundColor: sp.isDefault ? '#e3f2fd' : undefined }]}>
                                                    <Text style={[styles.nodeLabel, { width: '25%', fontSize: 6.5 }]}>{sp.speciesName}</Text>
                                                    <Text style={[styles.nodeValue, { width: '20%', fontSize: 6.5 }]}>{fmt(sp.timberTotal)}</Text>
                                                    <Text style={[styles.nodeValue, { width: '20%', fontSize: 6.5 }]}>{fmt(sp.materialsTotal)}</Text>
                                                    <Text style={[styles.nodeValue, { width: '20%', fontSize: 6.5 }]}>{fmt(sp.timberKitRaw)}</Text>
                                                    <Text style={[styles.nodeValue, { width: '15%', fontSize: 6.5 }]}>{sp.isDefault ? '✓' : ''}</Text>
                                                </View>
                                            ))}
                                        </>
                                    ) : (
                                        <Text style={{ fontSize: 7, color: '#999' }}>No alternate species configured</Text>
                                    )}
                                </View>
                            ))}
                        </View>
                    </>
                )}

                <Text style={styles.footer}>INTERNAL DEBUG REPORT - NOT FOR DISTRIBUTION</Text>
            </Page>

            {/* PAGE 9: ROUNDING AUDIT + QUANTITY VERIFICATION */}
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <Text style={styles.title}>Rounding Audit &amp; Quantity Verification</Text>
                    <Text style={styles.subtitle}>Pre-round vs post-round values for every bucket, plus quantity multiplier trace</Text>
                </View>

                {/* QUANTITY VERIFICATION */}
                <View style={[styles.structureBox, { marginBottom: 10 }]}>
                    <Text style={[styles.structureTitle, { marginBottom: 4 }]}>📦 Quantity Multiplier Trace</Text>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '30%', fontWeight: 'bold', fontSize: 7 }]}>Structure</Text>
                        <Text style={[styles.nodeValue, { width: '10%', fontWeight: 'bold', fontSize: 7 }]}>Qty</Text>
                        <Text style={[styles.nodeValue, { width: '20%', fontWeight: 'bold', fontSize: 7 }]}>Raw (1×)</Text>
                        <Text style={[styles.nodeValue, { width: '20%', fontWeight: 'bold', fontSize: 7 }]}>Raw (×qty)</Text>
                        <Text style={[styles.nodeValue, { width: '20%', fontWeight: 'bold', fontSize: 7 }]}>Billable (×qty)</Text>
                    </View>
                    {debugData.structures.map((s, i) => (
                        <View key={s.id} style={[styles.nodeRow, { backgroundColor: s.quantity > 1 ? '#fff3e0' : undefined }]}>
                            <Text style={[styles.nodeLabel, { width: '30%', fontSize: 7 }]}>{s.name}</Text>
                            <Text style={[styles.nodeValue, { width: '10%', fontSize: 7, fontWeight: 'bold', color: s.quantity > 1 ? '#e65100' : undefined }]}>
                                {s.quantity}{s.quantity > 1 ? ' ⚠' : ''}
                            </Text>
                            <Text style={[styles.nodeValue, { width: '20%', fontSize: 7 }]}>{fmt(s.structureRawTotal)}</Text>
                            <Text style={[styles.nodeValue, { width: '20%', fontSize: 7 }]}>{fmt(s.structureRawTotalWithQty)}</Text>
                            <Text style={[styles.nodeValue, { width: '20%', fontSize: 7 }]}>
                                {fmt(Math.round(applyMargin(s.structureRawTotalWithQty, debugData.profitMarginPercent)))}
                            </Text>
                        </View>
                    ))}
                    <View style={[styles.nodeRow, { backgroundColor: '#f5f5f5' }]}>
                        <Text style={[styles.nodeLabel, { width: '30%', fontWeight: 'bold', fontSize: 7 }]}>TOTAL</Text>
                        <Text style={[styles.nodeValue, { width: '10%', fontSize: 7 }]}></Text>
                        <Text style={[styles.nodeValue, { width: '20%', fontWeight: 'bold', fontSize: 7 }]}>
                            {fmt(debugData.structures.reduce((sum, s) => sum + s.structureRawTotal, 0))}
                        </Text>
                        <Text style={[styles.nodeValue, { width: '20%', fontWeight: 'bold', fontSize: 7 }]}>
                            {fmt(debugData.buckets.grandTotalRaw)}
                        </Text>
                        <Text style={[styles.nodeValue, { width: '20%', fontWeight: 'bold', fontSize: 7 }]}>
                            {fmt(debugData.buckets.grandTotalBillable)}
                        </Text>
                    </View>
                </View>

                {/* ROUNDING AUDIT */}
                <View style={[styles.structureBox, { marginBottom: 10 }]}>
                    <Text style={[styles.structureTitle, { marginBottom: 4 }]}>🔢 Rounding Audit — Per Structure</Text>
                    <Text style={{ fontSize: 7, color: '#666', marginBottom: 6 }}>
                        Shows exact pre-round value, post-round value, and rounding loss for each bucket.
                        Key metric: &quot;Sep vs Comb&quot; shows the $ difference between rounding install &amp; travel separately vs combined.
                    </Text>

                    {debugData.roundingAudit.map((r, i) => (
                        <View key={r.structureId} style={{ marginBottom: i < debugData.roundingAudit.length - 1 ? 8 : 0 }}>
                            <Text style={{ fontSize: 7.5, fontWeight: 'bold', color: '#333', marginBottom: 2 }}>
                                {r.structureName} (qty: {r.qty})
                            </Text>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '18%', fontWeight: 'bold', fontSize: 6.5 }]}>Bucket</Text>
                                <Text style={[styles.nodeValue, { width: '27%', fontWeight: 'bold', fontSize: 6.5 }]}>Pre-Round</Text>
                                <Text style={[styles.nodeValue, { width: '22%', fontWeight: 'bold', fontSize: 6.5 }]}>Post-Round</Text>
                                <Text style={[styles.nodeValue, { width: '18%', fontWeight: 'bold', fontSize: 6.5 }]}>Rounding</Text>
                                <Text style={[styles.nodeValue, { width: '15%', fontWeight: 'bold', fontSize: 6.5 }]}>Loss $</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '18%', fontSize: 6.5 }]}>Design</Text>
                                <Text style={[styles.nodeValue, { width: '27%', fontSize: 6.5 }]}>{r.designPreRound.toFixed(2)}</Text>
                                <Text style={[styles.nodeValue, { width: '22%', fontSize: 6.5 }]}>{fmt(r.designPostRound)}</Text>
                                <Text style={[styles.nodeValue, { width: '18%', fontSize: 6.5 }]}>{r.designRoundingLoss >= 0 ? '+' : ''}{r.designRoundingLoss.toFixed(2)}</Text>
                                <Text style={[styles.nodeValue, { width: '15%', fontSize: 6.5, color: Math.abs(r.designRoundingLoss) > 0.01 ? '#e65100' : '#999' }]}>
                                    {fmt(r.designRoundingLoss)}
                                </Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '18%', fontSize: 6.5 }]}>TimberKit</Text>
                                <Text style={[styles.nodeValue, { width: '27%', fontSize: 6.5 }]}>{r.timberKitPreRound.toFixed(2)}</Text>
                                <Text style={[styles.nodeValue, { width: '22%', fontSize: 6.5 }]}>{fmt(r.timberKitPostRound)}</Text>
                                <Text style={[styles.nodeValue, { width: '18%', fontSize: 6.5 }]}>{r.timberKitRoundingLoss >= 0 ? '+' : ''}{r.timberKitRoundingLoss.toFixed(2)}</Text>
                                <Text style={[styles.nodeValue, { width: '15%', fontSize: 6.5, color: Math.abs(r.timberKitRoundingLoss) > 0.01 ? '#e65100' : '#999' }]}>
                                    {fmt(r.timberKitRoundingLoss)}
                                </Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '18%', fontSize: 6.5 }]}>Install (sep)</Text>
                                <Text style={[styles.nodeValue, { width: '27%', fontSize: 6.5 }]}>{r.installPreRound.toFixed(2)}</Text>
                                <Text style={[styles.nodeValue, { width: '22%', fontSize: 6.5 }]}>{fmt(r.installPostRound)}</Text>
                                <Text style={[styles.nodeValue, { width: '18%', fontSize: 6.5 }]}>{r.installRoundingLoss >= 0 ? '+' : ''}{r.installRoundingLoss.toFixed(2)}</Text>
                                <Text style={[styles.nodeValue, { width: '15%', fontSize: 6.5 }]}>{fmt(r.installRoundingLoss)}</Text>
                            </View>
                            <View style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '18%', fontSize: 6.5 }]}>Travel (sep)</Text>
                                <Text style={[styles.nodeValue, { width: '27%', fontSize: 6.5 }]}>{r.travelPreRound.toFixed(2)}</Text>
                                <Text style={[styles.nodeValue, { width: '22%', fontSize: 6.5 }]}>{fmt(r.travelPostRound)}</Text>
                                <Text style={[styles.nodeValue, { width: '18%', fontSize: 6.5 }]}>{r.travelRoundingLoss >= 0 ? '+' : ''}{r.travelRoundingLoss.toFixed(2)}</Text>
                                <Text style={[styles.nodeValue, { width: '15%', fontSize: 6.5 }]}>{fmt(r.travelRoundingLoss)}</Text>
                            </View>
                            <View style={[styles.nodeRow, { backgroundColor: '#f5f5f5' }]}>
                                <Text style={[styles.nodeLabel, { width: '18%', fontSize: 6.5 }]}>Inst+Trav (comb)</Text>
                                <Text style={[styles.nodeValue, { width: '27%', fontSize: 6.5 }]}>{r.installCombinedPreRound.toFixed(2)}</Text>
                                <Text style={[styles.nodeValue, { width: '22%', fontSize: 6.5 }]}>{fmt(r.installCombinedPostRound)}</Text>
                                <Text style={[styles.nodeValue, { width: '18%', fontSize: 6.5 }]}>{r.installCombinedRoundingLoss >= 0 ? '+' : ''}{r.installCombinedRoundingLoss.toFixed(2)}</Text>
                                <Text style={[styles.nodeValue, { width: '15%', fontSize: 6.5 }]}>{fmt(r.installCombinedRoundingLoss)}</Text>
                            </View>
                            <View style={[styles.nodeRow, { backgroundColor: r.roundingDiffSeparateVsCombined !== 0 ? '#fff3e0' : '#e8f5e9' }]}>
                                <Text style={[styles.nodeLabel, { width: '18%', fontWeight: 'bold', fontSize: 6.5 }]}>Sep vs Comb</Text>
                                <Text style={[styles.nodeValue, { width: '82%', fontWeight: 'bold', fontSize: 6.5, color: r.roundingDiffSeparateVsCombined !== 0 ? '#e65100' : '#2e7d32' }]}>
                                    round(install)={fmt(r.installPostRound)} + round(travel)={fmt(r.travelPostRound)} = {fmt(r.installPostRound + r.travelPostRound)}
                                    {'  vs  '}
                                    round(install+travel)={fmt(r.installCombinedPostRound)}
                                    {'  →  '}
                                    Diff: {fmt(r.roundingDiffSeparateVsCombined)}
                                    {r.roundingDiffSeparateVsCombined !== 0 ? '  ⚠ THIS CAUSES DISCREPANCY' : '  ✓ no rounding diff'}
                                </Text>
                            </View>
                        </View>
                    ))}

                    <View style={[styles.nodeRow, { backgroundColor: debugData.totalRoundingImpact !== 0 ? '#ffebee' : '#e8f5e9', marginTop: 4 }]}>
                        <Text style={[styles.nodeLabel, { width: '30%', fontWeight: 'bold', fontSize: 7 }]}>Total Rounding Impact</Text>
                        <Text style={[styles.nodeValue, { width: '70%', fontWeight: 'bold', fontSize: 7, color: debugData.totalRoundingImpact !== 0 ? '#c62828' : '#2e7d32' }]}>
                            {fmt(debugData.totalRoundingImpact)}
                            {debugData.totalRoundingImpact !== 0
                                ? '  — save-time (separate) and PDF/public fallback (combined) totals will differ by this amount'
                                : '  ✓ No rounding discrepancy between paths'}
                        </Text>
                    </View>
                </View>

                <Text style={styles.footer}>INTERNAL DEBUG REPORT - NOT FOR DISTRIBUTION</Text>
            </Page>

            {/* PAGE 10: FIRESTORE DOCUMENT SNAPSHOT */}
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <Text style={styles.title}>Firestore Document Snapshot</Text>
                    <Text style={styles.subtitle}>Key fields from the stored proposal document — saves opening Firebase console</Text>
                </View>

                <View style={[styles.structureBox, { marginBottom: 10 }]}>
                    <Text style={[styles.structureTitle, { marginBottom: 4 }]}>📋 Stored Document Fields</Text>
                    <Text style={{ fontSize: 7, color: '#666', marginBottom: 6 }}>
                        These are the values currently saved in Firestore for this proposal. &quot;N/A&quot; means the field was not available at debug time.
                    </Text>
                    <View style={styles.nodeRow}>
                        <Text style={[styles.nodeLabel, { width: '35%', fontWeight: 'bold', fontSize: 7 }]}>Field</Text>
                        <Text style={[styles.nodeValue, { width: '30%', fontWeight: 'bold', fontSize: 7 }]}>Stored Value</Text>
                        <Text style={[styles.nodeValue, { width: '35%', fontWeight: 'bold', fontSize: 7 }]}>Notes</Text>
                    </View>

                    {Object.entries(debugData.firestoreSnapshot).map(([key, value]) => {
                        let displayValue: string;
                        let note = '';

                        if (value === undefined || value === null) {
                            displayValue = 'N/A';
                        } else if (typeof value === 'boolean') {
                            displayValue = value ? 'TRUE' : 'FALSE';
                        } else if (typeof value === 'number') {
                            displayValue = key.toLowerCase().includes('total') || key.toLowerCase().includes('margin')
                                ? (key.toLowerCase().includes('margin') ? `${value}%` : fmt(value))
                                : String(value);
                        } else if (Array.isArray(value)) {
                            displayValue = value.length > 0 ? value.join(', ') : '(empty array)';
                        } else {
                            displayValue = String(value);
                        }

                        // Add context notes
                        if (key === 'displayTotal' && value != null) {
                            const diff = (value as number) - debugData.recalculatedGrandTotal;
                            note = diff === 0 ? '✓ matches recalc' : `${fmt(diff)} vs recalc`;
                        } else if (key === 'profitMargin') {
                            note = `multiplier: ${debugData.marginMultiplier.toFixed(4)}×`;
                        } else if (key === 'slug' && value) {
                            note = `URL: /proposal/${value}`;
                        }

                        return (
                            <View key={key} style={styles.nodeRow}>
                                <Text style={[styles.nodeLabel, { width: '35%', fontSize: 7 }]}>{key}</Text>
                                <Text style={[styles.nodeValue, { width: '30%', fontSize: 7, fontWeight: 'bold' }]}>{displayValue}</Text>
                                <Text style={[styles.nodeValue, { width: '35%', fontSize: 6.5, color: '#666' }]}>{note}</Text>
                            </View>
                        );
                    })}
                </View>

                {/* NODE TOTAL SNAPSHOT — detect if data changed since last save */}
                <View style={[styles.structureBox, { marginBottom: 10 }]}>
                    <Text style={[styles.structureTitle, { marginBottom: 4 }]}>🔍 Node Change Detection — billablePrices vs Current Nodes</Text>
                    <Text style={{ fontSize: 7, color: '#666', marginBottom: 6 }}>
                        If a node total changed since the last save, billablePrices will be stale. This table reverse-engineers
                        what node totals WOULD produce the stored billablePrices, then compares to current nodes.
                    </Text>
                    {debugData.structures.map((s, si) => {
                        const stored = debugData.storedBillablePrices.find(sp => sp.structureId === s.id);
                        if (!stored) return null;
                        const margin = debugData.profitMarginPercent;
                        const qty = s.quantity;

                        // Reverse-engineer what raw total would produce stored billable
                        // stored = round(raw / (1 - margin/100) * qty)
                        // So raw ≈ stored * (1 - margin/100) / qty
                        const reverseMargin = (1 - margin / 100);
                        const impliedDesignRaw = stored.design * reverseMargin / qty;
                        const impliedTimberKitRaw = stored.timberKit * reverseMargin / qty;
                        const impliedInstallRaw = (stored.install + stored.travel) * reverseMargin / qty;

                        const actualDesign = s.designRaw;
                        const actualTimberKit = s.productionRaw + s.materialsRaw + s.freightRaw + s.overheadRaw;
                        const actualInstall = s.installRaw + s.travelRaw;

                        const designChanged = Math.abs(impliedDesignRaw - actualDesign) > 1;
                        const tkChanged = Math.abs(impliedTimberKitRaw - actualTimberKit) > 1;
                        const installChanged = Math.abs(impliedInstallRaw - actualInstall) > 1;
                        const anyChanged = designChanged || tkChanged || installChanged;

                        return (
                            <View key={s.id} style={{ marginBottom: si < debugData.structures.length - 1 ? 6 : 0 }}>
                                <Text style={{ fontSize: 7.5, fontWeight: 'bold', color: anyChanged ? '#c62828' : '#2e7d32', marginBottom: 2 }}>
                                    {s.name} — {anyChanged ? '⚠ NODES CHANGED SINCE LAST SAVE' : '✓ Nodes match stored billablePrices'}
                                </Text>
                                {anyChanged && (
                                    <>
                                        <View style={styles.nodeRow}>
                                            <Text style={[styles.nodeLabel, { width: '25%', fontWeight: 'bold', fontSize: 6.5 }]}>Bucket</Text>
                                            <Text style={[styles.nodeValue, { width: '25%', fontWeight: 'bold', fontSize: 6.5 }]}>Implied Raw</Text>
                                            <Text style={[styles.nodeValue, { width: '25%', fontWeight: 'bold', fontSize: 6.5 }]}>Actual Raw</Text>
                                            <Text style={[styles.nodeValue, { width: '25%', fontWeight: 'bold', fontSize: 6.5 }]}>Diff</Text>
                                        </View>
                                        {designChanged && (
                                            <View style={[styles.nodeRow, { backgroundColor: '#ffebee' }]}>
                                                <Text style={[styles.nodeLabel, { width: '25%', fontSize: 6.5 }]}>Design</Text>
                                                <Text style={[styles.nodeValue, { width: '25%', fontSize: 6.5 }]}>{fmt(impliedDesignRaw)}</Text>
                                                <Text style={[styles.nodeValue, { width: '25%', fontSize: 6.5 }]}>{fmt(actualDesign)}</Text>
                                                <Text style={[styles.nodeValue, { width: '25%', fontSize: 6.5, color: '#c62828' }]}>{fmt(actualDesign - impliedDesignRaw)}</Text>
                                            </View>
                                        )}
                                        {tkChanged && (
                                            <View style={[styles.nodeRow, { backgroundColor: '#ffebee' }]}>
                                                <Text style={[styles.nodeLabel, { width: '25%', fontSize: 6.5 }]}>TimberKit</Text>
                                                <Text style={[styles.nodeValue, { width: '25%', fontSize: 6.5 }]}>{fmt(impliedTimberKitRaw)}</Text>
                                                <Text style={[styles.nodeValue, { width: '25%', fontSize: 6.5 }]}>{fmt(actualTimberKit)}</Text>
                                                <Text style={[styles.nodeValue, { width: '25%', fontSize: 6.5, color: '#c62828' }]}>{fmt(actualTimberKit - impliedTimberKitRaw)}</Text>
                                            </View>
                                        )}
                                        {installChanged && (
                                            <View style={[styles.nodeRow, { backgroundColor: '#ffebee' }]}>
                                                <Text style={[styles.nodeLabel, { width: '25%', fontSize: 6.5 }]}>Install+Travel</Text>
                                                <Text style={[styles.nodeValue, { width: '25%', fontSize: 6.5 }]}>{fmt(impliedInstallRaw)}</Text>
                                                <Text style={[styles.nodeValue, { width: '25%', fontSize: 6.5 }]}>{fmt(actualInstall)}</Text>
                                                <Text style={[styles.nodeValue, { width: '25%', fontSize: 6.5, color: '#c62828' }]}>{fmt(actualInstall - impliedInstallRaw)}</Text>
                                            </View>
                                        )}
                                    </>
                                )}
                            </View>
                        );
                    })}
                </View>

                <Text style={styles.footer}>INTERNAL DEBUG REPORT - NOT FOR DISTRIBUTION</Text>
            </Page>

            {/* ============================================================ */}
            {/* PAGE: GLOBAL PROJECT COSTS                                    */}
            {/* ============================================================ */}
            {(() => {
                const globalDollarLines = debugData.globalCosts?.totalGlobalCosts || 0;
                const estimatingAmount = estimatingRaw(debugData.designEstimating, debugData.rates);
                const perStructure = debugData.structureCount > 0 ? globalDollarLines / debugData.structureCount : 0;
                if (globalDollarLines <= 0 && estimatingAmount <= 0) return null;
                return (
                <Page size="LETTER" style={styles.page}>
                    <View style={styles.header}>
                        <Text style={styles.title}>Global Project Costs</Text>
                        <Text style={styles.subtitle}>
                            {proposalName || 'Proposal'} — {clientName || 'Client'}
                        </Text>
                    </View>

                    {/* Explanation */}
                    <View style={[styles.infoBox, { marginBottom: 10 }]}>
                        <Text style={{ fontSize: 8, color: '#1565c0' }}>
                            Global costs are project-level expenses distributed evenly across all structures into the overhead bucket.
                            They are absorbed into the timber kit price the customer sees — not shown as a separate line item.
                        </Text>
                    </View>

                    {listEstimatingAuditLines(debugData.designEstimating, debugData.rates).length > 0 && (
                        <View style={[styles.structureBox, { marginBottom: 10 }]}>
                            <Text style={styles.structureTitle}>Estimating / modeling</Text>
                            {listEstimatingAuditLines(debugData.designEstimating, debugData.rates).map((line) => (
                                <View key={line.key} style={styles.nodeRow}>
                                    <Text style={[styles.nodeLabel, { width: '60%' }]}>{line.label}: {line.detail}</Text>
                                    <Text style={[styles.nodeValue, { width: '40%' }]}>{fmt(line.amount)}</Text>
                                </View>
                            ))}
                            <View style={[styles.nodeRow, { backgroundColor: '#f5f5f5' }]}>
                                <Text style={[styles.nodeLabel, { width: '60%', fontWeight: 'bold' }]}>Estimating total</Text>
                                <Text style={[styles.nodeTotal, { width: '40%' }]}>
                                    {fmt(estimatingAmount)}
                                </Text>
                            </View>
                        </View>
                    )}

                    {/* Summary row */}
                    <View style={[styles.summaryBox, styles.summaryRow, { marginBottom: 10 }]}>
                        <View>
                            <Text style={styles.summaryLabel}>Total Global Costs</Text>
                            <Text style={styles.summaryValue}>{fmt(globalDollarLines)}</Text>
                        </View>
                        <View>
                            <Text style={styles.summaryLabel}>Per Structure ({debugData.structureCount})</Text>
                            <Text style={styles.summaryValue}>{fmt(perStructure)}</Text>
                        </View>
                        <View>
                            <Text style={styles.summaryLabel}>After Margin ({debugData.profitMarginPercent}%)</Text>
                            <Text style={styles.summaryValue}>{fmt(applyMargin(perStructure, debugData.marginMultiplier) * debugData.structureCount)}</Text>
                        </View>
                    </View>

                    {/* Individual cost items */}
                    <View style={styles.structureBox}>
                        <Text style={styles.structureTitle}>Cost Item Breakdown</Text>

                        {/* Header row */}
                        <View style={[styles.nodeRow, { backgroundColor: '#e0e0e0' }]}>
                            <Text style={[styles.nodeLabel, { width: '30%', fontWeight: 'bold' }]}>Item</Text>
                            <Text style={[styles.nodeValue, { width: '15%', fontWeight: 'bold' }]}>Status</Text>
                            <Text style={[styles.nodeValue, { width: '20%', fontWeight: 'bold' }]}>Amount</Text>
                            <Text style={[styles.nodeLabel, { width: '35%', fontWeight: 'bold' }]}>Notes</Text>
                        </View>

                        {/* Cost items */}
                        {([
                            { key: 'baselineTravel', label: 'Baseline Travel' },
                            { key: 'additionalTravel', label: 'Additional Travel' },
                            { key: 'globalFreightInbound', label: 'Freight — Inbound' },
                            { key: 'globalFreightOutbound', label: 'Freight — Outbound' },
                            { key: 'equipmentRental', label: 'Equipment Rental' },
                            { key: 'additionalGear', label: 'Additional Gear' },
                            { key: 'ppeBudget', label: 'PPE Budget' },
                            { key: 'jobsiteIncidentals', label: 'Jobsite Incidentals' },
                            { key: 'additionalInsurance', label: 'Additional Insurance' },
                            { key: 'damagedTimberBudget', label: 'Damaged Timber Budget' },
                            { key: 'jobsiteSecurity', label: 'Jobsite Security' },
                            { key: 'additionalVehicles', label: 'Additional Vehicles' },
                            { key: 'jobSpecificTooling', label: 'Job-Specific Tooling' },
                        ] as Array<{ key: string; label: string }>).map((item) => {
                            const costItem = (debugData.globalCosts as any)?.[item.key];
                            if (!costItem) return null;
                            return (
                                <View key={item.key} style={[styles.nodeRow, { backgroundColor: costItem.enabled ? '#f0fdf4' : '#fafafa' }]}>
                                    <Text style={[styles.nodeLabel, { width: '30%' }]}>{item.label}</Text>
                                    <Text style={[styles.nodeValue, { width: '15%', color: costItem.enabled ? '#16a34a' : '#94a3b8' }]}>
                                        {costItem.enabled ? '✓ ON' : '— OFF'}
                                    </Text>
                                    <Text style={[styles.nodeValue, { width: '20%', fontWeight: costItem.enabled ? 'bold' : 'normal' }]}>
                                        {costItem.enabled ? fmt(costItem.amount) : '—'}
                                    </Text>
                                    <Text style={[styles.nodeLabel, { width: '35%', color: '#64748b', fontSize: 7 }]}>
                                        {costItem.notes || ''}
                                    </Text>
                                </View>
                            );
                        })}

                        {/* Total row */}
                        <View style={[styles.nodeRow, { backgroundColor: '#1e293b', marginTop: 4 }]}>
                            <Text style={[styles.nodeLabel, { width: '30%', color: '#fff', fontWeight: 'bold' }]}>TOTAL</Text>
                            <Text style={[styles.nodeValue, { width: '15%' }]}></Text>
                            <Text style={[styles.nodeValue, { width: '20%', color: '#fff', fontWeight: 'bold' }]}>
                                {fmt(globalDollarLines)}
                            </Text>
                            <Text style={[styles.nodeLabel, { width: '35%' }]}></Text>
                        </View>
                    </View>

                    {/* Distribution summary */}
                    <View style={[styles.infoBox, { marginTop: 10 }]}>
                        <Text style={{ fontSize: 8, color: '#1565c0', fontWeight: 'bold', marginBottom: 3 }}>Distribution:</Text>
                        <Text style={{ fontSize: 8, color: '#1565c0' }}>
                            {fmt(globalDollarLines)} ÷ {debugData.structureCount} structures = {fmt(perStructure)} per structure added to overhead
                        </Text>
                        <Text style={{ fontSize: 8, color: '#1565c0' }}>
                            After {debugData.profitMarginPercent}% margin: {fmt(applyMargin(perStructure, debugData.marginMultiplier))} per structure billable impact
                        </Text>
                    </View>

                    <Text style={styles.footer}>INTERNAL DEBUG REPORT - NOT FOR DISTRIBUTION</Text>
                </Page>
                );
            })()}
        </Document >
    );
};

export default PricingDebugPdfDocument;
